import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import * as d3 from 'd3';
import MobileChartWrapper from './mobile/MobileChartWrapper';
import MobilePredictionsList from './mobile/MobilePredictionsList';
import { Prediction } from '../types/predictions';
import { darkTheme } from '../theme';

// Umami analytics helper
declare global {
  interface Window {
    umami?: {
      track: (eventName: string, eventData?: Record<string, string | number | boolean>) => void;
    };
  }
}

const trackEvent = (eventName: string, eventData?: Record<string, string | number | boolean>) => {
  if (window.umami) {
    window.umami.track(eventName, eventData);
  }
};

// Type definitions
interface TweetStats {
  total_tweets: number;
  bitcoin_tweets: number;
  first_tweet: string;
  total_likes: number;
  max_likes: number;
}

interface TopTweet {
  tweet_text: string;
  like_count: number;
}

interface AnalyticsData {
  stats: TweetStats;
  topTweets?: TopTweet[];
  grokAnalysis?: string;
}

interface UserProfile {
  profile_image?: string;
  display_name?: string;
}

interface BitcoinJourney {
  yearlyProgression: Array<{
    year: string;
    total_tweets_year: number;
    bitcoin_tweets_year: number;
    bitcoin_percentage_year: number;
  }>;
  firstBitcoinTweet?: {
    tweet_created_at: string;
    tweet_text: string;
    like_count: number;
  };
  firstBitcoinTweets?: Array<{
    tweet_created_at: string;
    tweet_text: string;
    like_count: number;
    tweet_url: string;
  }>;
  monthlyEvolution: Array<{
    month: string;
    bitcoin_tweets: number;
    avg_engagement: number;
  }>;
}

interface PredictionData {
  hasPredictions: boolean;
  stats: {
    total_predictions: number;
    successful_predictions: number;
    success_rate: number;
    avg_vote_score: number;
  };
  recentPredictions: Array<{
    price_prediction: string;
    success_status: string;
    quote: string;
    created_at: string;
  }>;
}

const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    background: #000;
    color: #fff;
    overflow-x: hidden;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes loading-progress {
    0% { transform: translateX(-100%); }
    50% { transform: translateX(0%); }
    100% { transform: translateX(100%); }
  }
  
  /* Smooth scroll snap behavior */
  .scroll-container {
    scroll-snap-type: y mandatory;
    scroll-snap-stop: always;
  }
  
  .scroll-container > div {
    scroll-snap-align: start;
  }

  /* Hide scrollbar but keep functionality */
  .scroll-container {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer 10+ */
  }
  
  .scroll-container::-webkit-scrollbar {
    display: none; /* WebKit */
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const snowfall = keyframes`
  0% {
    transform: translateY(-10vh) translateX(0) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(110vh) translateX(20px) rotate(360deg);
    opacity: 0;
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
  background: 
    linear-gradient(135deg, #8B0000 0%, #A52A2A 50%, #8B0000 100%),
    url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"><g transform="translate(50,50)"><path d="M50,50 L50,200 M50,50 L120,50 Q150,50 150,80 Q150,100 130,110 Q160,110 160,140 Q160,170 120,170 L50,170 M50,110 L130,110 M40,40 L60,40 M40,210 L60,210 M40,180 L60,180 M40,20 L60,20" stroke="%23ffffff" stroke-width="8" fill="none" opacity="0.15"/></g><g transform="translate(200,150)"><path d="M50,50 L50,200 M50,50 L120,50 Q150,50 150,80 Q150,100 130,110 Q160,110 160,140 Q160,170 120,170 L50,170 M50,110 L130,110 M40,40 L60,40 M40,210 L60,210 M40,180 L60,180 M40,20 L60,20" stroke="%23ffffff" stroke-width="6" fill="none" opacity="0.12"/></g><g transform="translate(300,250)"><path d="M50,50 L50,200 M50,50 L120,50 Q150,50 150,80 Q150,100 130,110 Q160,110 160,140 Q160,170 120,170 L50,170 M50,110 L130,110 M40,40 L60,40 M40,210 L60,210 M40,180 L60,180 M40,20 L60,20" stroke="%23ffffff" stroke-width="5" fill="none" opacity="0.08"/></g></svg>');
  background-repeat: repeat;
  background-position: 0 0;
  background-attachment: fixed;
  color: #fff;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  position: relative;
  overflow-x: hidden;
  margin: 0;
  padding: 0;
`;

// Logo Component
const LogoContainer = styled.div`
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 1000;
  
  img {
    height: 60px;
    width: auto;
    opacity: 0.9;
    transition: opacity 0.3s ease;
    cursor: pointer;
    
    &:hover {
      opacity: 1;
    }
  }
  
  @media (max-width: 768px) {
    top: 15px;
    left: 15px;
    
    img {
      height: 48px;
    }
  }
`;

const StoryContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
  
  @media (min-width: 768px) {
    max-width: 420px;
  }
`;

interface SlideContainerProps {
  background?: string;
}

const SlideContainer = styled.div<SlideContainerProps>`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 15px;
  position: relative;
  background: ${props => props.background || 'transparent'};
  animation: ${fadeInUp} 0.8s ease-out;
  z-index: 1;
  
  @media (min-width: 768px) {
    padding: 30px;
  }
`;

// Typography
const BigTitle = styled.h1`
  font-size: clamp(2rem, 7vw, 3.5rem);
  font-weight: 900;
  text-align: center;
  margin-bottom: 12px;
  background: linear-gradient(45deg, #f7931a, #ffeb3b, #f7931a);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${gradientShift} 3s ease infinite;
  line-height: 1.1;
`;

const SubTitle = styled.h2`
  font-size: clamp(1rem, 3.5vw, 1.6rem);
  font-weight: 600;
  text-align: center;
  margin-bottom: 18px;
  color: #fff;
  opacity: 0.9;
`;

const StoryText = styled.p`
  font-size: clamp(0.9rem, 2.5vw, 1.1rem);
  line-height: 1.5;
  text-align: center;
  margin: 12px 0;
  color: #e0e0e0;
  max-width: 380px;
`;

const StatNumber = styled.div`
  font-size: clamp(2.5rem, 8vw, 5rem);
  font-weight: 900;
  text-align: center;
  margin: 12px 0;
  background: linear-gradient(45deg, #f7931a, #ffeb3b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${pulse} 2s ease infinite;
`;

const StatLabel = styled.div`
  font-size: clamp(0.9rem, 3vw, 1.1rem);
  font-weight: 500;
  text-align: center;
  color: #b0b0b0;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

// Input Components
const InputContainer = styled.div`
  width: 100%;
  max-width: 340px;
  margin: 20px 0;
`;

const UsernameInput = styled.input`
  width: 100%;
  padding: 18px 24px;
  font-size: 1.1rem;
  font-size: 16px; /* Prevents iOS zoom on focus */
  border: 2px solid #333;
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  text-align: center;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  -webkit-appearance: none; /* Removes iOS default styling */
  -webkit-user-select: text; /* Ensures text selection works */
  user-select: text;
  touch-action: manipulation; /* Improves touch responsiveness */
  
  &:focus {
    outline: none;
    border-color: #f7931a;
    background: rgba(255, 255, 255, 0.15);
  }
  
  &::placeholder {
    color: #888;
  }
`;

const AnalyzeButton = styled.button`
  width: 100%;
  padding: 18px 24px;
  margin-top: 20px;
  font-size: 1.1rem;
  font-weight: 600;
  background: linear-gradient(45deg, #f7931a, #ffeb3b);
  border: none;
  border-radius: 50px;
  color: #000;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(247, 147, 26, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// Progress Components
const ProgressContainer = styled.div`
  width: 100%;
  max-width: 300px;
  margin: 20px 0;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 15px;
`;

interface ProgressFillProps {
  progress: number;
}

const ProgressFill = styled.div<ProgressFillProps>`
  height: 100%;
  background: linear-gradient(90deg, #f7931a, #ffeb3b);
  border-radius: 3px;
  width: ${props => props.progress}%;
  transition: width 0.5s ease;
`;

const ProgressText = styled.div`
  text-align: center;
  font-size: 0.9rem;
  color: #b0b0b0;
`;

// Card Components
const InsightCard = styled.div`
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(30, 30, 30, 0.9) 100%);
  backdrop-filter: blur(20px);
  border-radius: 14px;
  padding: 18px;
  margin: 12px 0;
  width: 100%;
  max-width: 340px;
  border: 1px solid rgba(247, 147, 26, 0.2);
  box-shadow: 
    0 8px 30px rgba(0, 0, 0, 0.4),
    0 0 20px rgba(247, 147, 26, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(247, 147, 26, 0.4);
    box-shadow: 
      0 12px 40px rgba(0, 0, 0, 0.5),
      0 0 30px rgba(247, 147, 26, 0.15);
    transform: translateY(-2px);
  }
  
  /* Custom scrollbar for overflow content */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(45deg, #f7931a, #ffeb3b);
    border-radius: 3px;
  }
`;

// Twitter-style Tweet Card Components
const TweetCard = styled.div`
  background: rgba(20, 23, 26, 0.95);
  border: 1px solid rgba(56, 68, 77, 0.8);
  border-radius: 16px;
  padding: 16px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(25, 28, 31, 0.98);
    border-color: rgba(247, 147, 26, 0.4);
    box-shadow: 0 4px 20px rgba(247, 147, 26, 0.15);
  }
`;

const TweetHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const TweetAvatar = styled.div<{ profileImage?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.profileImage 
    ? `url(${props.profileImage}) center/cover` 
    : 'linear-gradient(45deg, #f7931a, #ffeb3b)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  color: #000;
  flex-shrink: 0;
`;

const TweetUserInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const TweetDisplayName = styled.div`
  font-weight: 700;
  font-size: 0.95rem;
  color: #e7e9ea;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TweetHandle = styled.div`
  font-size: 0.85rem;
  color: #71767b;
`;

const TweetText = styled.div`
  color: #e7e9ea;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 12px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const TweetStats = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  font-size: 0.85rem;
  color: #71767b;
  
  .likes {
    color: #f91880;
    font-weight: 500;
  }
`;

const PersonalityBadge = styled.div`
  display: inline-block;
  background: linear-gradient(45deg, #f7931a, #ffeb3b);
  color: #000;
  padding: 10px 20px;
  border-radius: 25px;
  font-weight: 600;
  font-size: 0.9rem;
  margin: 10px 5px;
  box-shadow: 0 4px 12px rgba(247, 147, 26, 0.3);
  border: 1px solid rgba(247, 147, 26, 0.5);
`;

// Navigation
const NavContainer = styled.div`
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 15px;
  z-index: 100;
`;

interface NavButtonProps {
  active?: boolean;
}

const NavButton = styled.button<NavButtonProps>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 2px solid #f7931a;
  background: ${props => props.active ? '#f7931a' : 'rgba(0,0,0,0.7)'};
  color: ${props => props.active ? '#000' : '#f7931a'};
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: #f7931a;
    color: #000;
    transform: scale(1.1);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const ShareButton = styled.button`
  background: linear-gradient(45deg, #1da1f2, #1991db);
  border: none;
  border-radius: 25px;
  padding: 15px 30px;
  color: white;
  font-weight: 600;
  margin-top: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(29, 161, 242, 0.3);
  }
`;

const BitcoinBadge = styled.div`
  display: inline-block;
  background: linear-gradient(45deg, #f7931a, #ffeb3b);
  color: #000;
  padding: 4px 12px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.75rem;
  margin-bottom: 10px;
`;

const SnowflakeContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: -1;
`;

const Snowflake = styled.div<{ delay: number; duration: number; left: number; size: number }>`
  position: absolute;
  top: -10vh;
  left: ${props => props.left}%;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  color: rgba(255, 255, 255, 0.6);
  font-size: ${props => props.size}px;
  animation: ${snowfall} ${props => props.duration}s linear ${props => props.delay}s infinite;
  user-select: none;
`;

const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 3px solid rgba(247, 147, 26, 0.3);
  border-radius: 50%;
  border-top-color: #f7931a;
  animation: spin 1s ease-in-out infinite;
  margin: 20px auto;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// ProofOfPrice Branding
const ProofOfPriceLogo = styled.div`
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 100;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  padding: 10px 15px;
  border-radius: 20px;
  border: 1px solid rgba(247, 147, 26, 0.3);
  
  @media (max-width: 768px) {
    top: 15px;
    left: 15px;
    padding: 8px 12px;
  }
`;

const LogoText = styled.div`
  color: #f7931a;
  font-weight: 700;
  font-size: 0.9rem;
  text-decoration: none;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

// Progress Bar
const ProgressBarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  z-index: 100;
`;

const ProgressBarFill = styled.div<ProgressFillProps>`
  height: 100%;
  background: linear-gradient(90deg, #f7931a, #ffeb3b);
  width: ${props => props.progress}%;
  transition: width 0.5s ease;
`;

// Touch Navigation - Full sides for easier navigation
const TouchArea = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  display: grid;
  grid-template-columns: 45% 10% 45%;
  pointer-events: none;
  
  > div {
    pointer-events: all;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: background 0.3s ease;
    
    &:first-child {
      justify-content: flex-start;
      padding-left: 30px;
      background: linear-gradient(90deg, rgba(0, 0, 0, 0.05) 0%, transparent 50%);
      
      &:hover {
        background: linear-gradient(90deg, rgba(0, 0, 0, 0.15) 0%, transparent 60%);
      }
      
      &:active {
        background: linear-gradient(90deg, rgba(247, 147, 26, 0.1) 0%, transparent 50%);
      }
    }
    
    &:nth-child(2) {
      pointer-events: none;
    }
    
    &:last-child {
      justify-content: flex-end;
      padding-right: 30px;
      background: linear-gradient(270deg, rgba(0, 0, 0, 0.05) 0%, transparent 50%);
      
      &:hover {
        background: linear-gradient(270deg, rgba(0, 0, 0, 0.15) 0%, transparent 60%);
      }
      
      &:active {
        background: linear-gradient(270deg, rgba(247, 147, 26, 0.1) 0%, transparent 50%);
      }
    }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 40% 20% 40%;
    
    > div {
      &:first-child {
        padding-left: 15px;
      }
      
      &:last-child {
        padding-right: 15px;
      }
      
      &:hover {
        background: transparent !important;
      }
    }
  }
`;

const TouchAreaIcon = styled.div`
  color: rgba(255, 255, 255, 0.2);
  font-size: 2.5rem;
  transition: all 0.3s ease;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  
  ${TouchArea} > div:hover & {
    color: rgba(255, 255, 255, 0.7);
    transform: scale(1.2);
  }
  
  ${TouchArea} > div:active & {
    color: #f7931a;
    transform: scale(1.1);
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
    
    ${TouchArea} > div:hover & {
      transform: none;
    }
  }
`;

const NavigationHint = styled.div`
  position: fixed;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
  z-index: 100;
  text-align: center;
  
  @media (max-width: 768px) {
    bottom: 80px;
    font-size: 0.75rem;
  }
`;

// Bar Chart Components
const ChartContainer = styled.div`
  width: 100%;
  max-width: 400px;
  height: 320px;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(25, 25, 25, 0.9) 100%);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 20px;
  margin: 30px auto;
  border: 1px solid rgba(247, 147, 26, 0.2);
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ChartTitle = styled.div`
  color: #f7931a;
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const ChartWrapper = styled.div`
  height: 250px;
  width: 100%;
  
  .recharts-bar {
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }
  
  .recharts-cartesian-axis-tick-value {
    fill: #c0c0c0;
    font-size: 12px;
    font-weight: 500;
  }
  
  .recharts-cartesian-grid-horizontal line {
    stroke: rgba(255, 255, 255, 0.05);
  }
`;

// D3 Bar Chart Component
const D3BarChart: React.FC<{
  data: Array<{year: number, totalTweets: number, bitcoinTweets: number, percentage: number}>,
  width?: number,
  height?: number
}> = ({ data, width = 400, height = 220 }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  
  useEffect(() => {
    if (!svgRef.current || !data.length) return;
    
    // Clear previous chart
    d3.select(svgRef.current).selectAll('*').remove();
    
    const svg = d3.select(svgRef.current);
    const margin = { top: 15, right: 60, bottom: 25, left: 20 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;
    
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);
    
    // Scales
    const xScale = d3.scaleBand()
      .domain(data.map(d => `'${String(d.year).slice(-2)}`))
      .range([0, chartWidth])
      .padding(0.25); // Increased padding to make bars thinner
    
    const maxTotal = d3.max(data, d => d.totalTweets) || 1;
    const yScale = d3.scaleLinear()
      .domain([0, maxTotal])
      .range([chartHeight, 0]);
    
    // Create gradient definitions
    const defs = svg.append('defs');
    
    const grayGradient = defs.append('linearGradient')
      .attr('id', 'grayGradient')
      .attr('x1', '0%').attr('y1', '100%')
      .attr('x2', '0%').attr('y2', '0%');
    grayGradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', 'rgba(120, 120, 120, 0.8)');
    grayGradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', 'rgba(180, 180, 180, 1)');
    
    const bitcoinGradient = defs.append('linearGradient')
      .attr('id', 'bitcoinGradient')
      .attr('x1', '0%').attr('y1', '100%')
      .attr('x2', '0%').attr('y2', '0%');
    bitcoinGradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#d84315');
    bitcoinGradient.append('stop')
      .attr('offset', '50%')
      .attr('stop-color', '#f7931a');
    bitcoinGradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#ffb74d');
    
    const bitcoinGradientBright = defs.append('linearGradient')
      .attr('id', 'bitcoinGradientBright')
      .attr('x1', '0%').attr('y1', '100%')
      .attr('x2', '0%').attr('y2', '0%');
    bitcoinGradientBright.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#e65100');
    bitcoinGradientBright.append('stop')
      .attr('offset', '25%')
      .attr('stop-color', '#f7931a');
    bitcoinGradientBright.append('stop')
      .attr('offset', '60%')
      .attr('stop-color', '#ffb74d');
    bitcoinGradientBright.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#ffeb3b');
    
    // Total tweets bars (background)
    g.selectAll('.total-bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'total-bar')
      .attr('x', d => xScale(`'${String(d.year).slice(-2)}`) || 0)
      .attr('width', xScale.bandwidth())
      .attr('y', d => yScale(d.totalTweets))
      .attr('height', d => chartHeight - yScale(d.totalTweets))
      .attr('fill', 'url(#grayGradient)')
      .attr('stroke', 'rgba(180, 180, 180, 0.8)')
      .attr('stroke-width', 1)
      .attr('rx', 4)
      .style('filter', 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))');
    
    // Bitcoin tweets bars (overlay)
    g.selectAll('.bitcoin-bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'bitcoin-bar')
      .attr('x', d => xScale(`'${String(d.year).slice(-2)}`) || 0)
      .attr('width', xScale.bandwidth())
      .attr('y', d => yScale(d.bitcoinTweets))
      .attr('height', d => chartHeight - yScale(d.bitcoinTweets))
      .attr('fill', d => d.year >= 2023 ? 'url(#bitcoinGradientBright)' : 'url(#bitcoinGradient)')
      .attr('stroke', 'rgba(247, 147, 26, 0.9)')
      .attr('stroke-width', 1)
      .attr('rx', 4)
      .style('filter', 'drop-shadow(0 2px 8px rgba(247, 147, 26, 0.4))');
    
    // X-axis labels
    g.append('g')
      .attr('transform', `translate(0,${chartHeight})`)
      .call(d3.axisBottom(xScale)
        .tickSize(0)
        .tickPadding(8))
      .call(g => {
        g.select('.domain').remove();
        g.selectAll('.tick text')
          .attr('fill', '#c0c0c0')
          .attr('font-size', '11px')
          .attr('font-weight', '600');
      });
    
    // Add hover effects
    g.selectAll('rect')
      .on('mouseover', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .style('filter', 'drop-shadow(0 4px 12px rgba(247, 147, 26, 0.6)) brightness(1.1)');
      })
      .on('mouseout', function() {
        d3.select(this)
          .transition()
          .duration(200)
          .style('filter', d3.select(this).classed('bitcoin-bar') 
            ? 'drop-shadow(0 2px 8px rgba(247, 147, 26, 0.4))'
            : 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))');
      });
    
    // Add tweet count labels on top of bars
    g.selectAll('.tweet-count-label')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'tweet-count-label')
      .attr('x', d => (xScale(`'${String(d.year).slice(-2)}`) || 0) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(d.totalTweets) - 5)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('font-weight', '600')
      .attr('fill', '#f7931a')
      .text(d => d.totalTweets);
    
  }, [data, width, height]);
  
  return (
    <svg 
      ref={svgRef} 
      width={width} 
      height={height}
      style={{ display: 'block', margin: '0 auto' }}
    />
  );
};



const ChartLegend = styled.div`
  font-size: 0.85rem;
  color: #e0e0e0;
  text-align: center;
  margin-top: 15px;
  
  .legend-item {
    display: inline-block;
    margin: 0 15px;
    
    &::before {
      content: '';
      display: inline-block;
      width: 16px;
      height: 10px;
      margin-right: 6px;
      border-radius: 3px;
      vertical-align: middle;
    }
    
    &.total::before {
      background: linear-gradient(135deg, rgba(180, 180, 180, 0.9), rgba(120, 120, 120, 0.7));
    }
    
    &.bitcoin::before {
      background: linear-gradient(135deg, #f7931a, #ffeb3b);
    }
  }
`;

// Scrollable Tweet List Components
const TweetList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  margin: 20px 0;
  padding-right: 10px;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(45deg, #f7931a, #ffeb3b);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(45deg, #ffeb3b, #f7931a);
  }
`;

const TweetListItem = styled.div`
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TweetRank = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(45deg, #f7931a, #ffeb3b);
  color: #000;
  font-size: 0.8rem;
  font-weight: bold;
  margin-bottom: 8px;
`;

const MiniTweetCard = styled.div`
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(247, 147, 26, 0.3);
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(247, 147, 26, 0.6);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(247, 147, 26, 0.2);
  }
`;

const MiniTweetHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const MiniTweetAvatar = styled.div<{profileImage?: string}>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.profileImage 
    ? `url(${props.profileImage}) center/cover no-repeat`
    : 'linear-gradient(45deg, #f7931a, #ffeb3b)'
  };
  color: ${props => props.profileImage ? 'transparent' : '#000'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: bold;
  flex-shrink: 0;
  overflow: hidden;
`;

const MiniTweetMeta = styled.div`
  flex: 1;
  font-size: 0.75rem;
  color: #888;
`;

const MiniTweetText = styled.div`
  color: #e0e0e0;
  font-size: 0.85rem;
  line-height: 1.3;
  margin-bottom: 8px;
`;

const MiniTweetStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: #888;
`;

const SubmitPrompt = styled.div`
  background: linear-gradient(45deg, rgba(247, 147, 26, 0.1), rgba(255, 235, 59, 0.1));
  border: 2px dashed rgba(247, 147, 26, 0.5);
  border-radius: 16px;
  padding: 20px;
  text-align: center;
  margin: 20px 0;
`;

const SubmitButton = styled.button`
  background: linear-gradient(45deg, #f7931a, #ffeb3b);
  border: none;
  border-radius: 25px;
  padding: 12px 24px;
  color: #000;
  font-weight: 600;
  margin-top: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(247, 147, 26, 0.3);
  }
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const AnalysisModal = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  border-radius: 20px;
  padding: 30px;
  max-width: 500px;
  width: 100%;
  border: 1px solid rgba(247, 147, 26, 0.3);
  box-shadow: 0 20px 60px rgba(247, 147, 26, 0.2);
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ModalTitle = styled.h3`
  color: #f7931a;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 20px;
  text-align: center;
`;

const KeywordGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  margin: 20px 0;
`;

const KeywordBadge = styled.div`
  background: linear-gradient(45deg, #f7931a, #ffeb3b);
  color: #000;
  padding: 8px 12px;
  border-radius: 15px;
  font-weight: 600;
  font-size: 0.8rem;
  text-align: center;
  text-transform: lowercase;
`;

const AnalysisSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 15px;
  margin: 15px 0;
`;

const AnalysisLabel = styled.div`
  color: #b0b0b0;
  font-size: 0.9rem;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const AnalysisText = styled.div`
  color: #e0e0e0;
  font-size: 1rem;
  line-height: 1.5;
`;

const BitcoinWrapped = () => {
  const { username: urlUsername } = useParams<{ username?: string }>();
  const [currentSection, setCurrentSection] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [username, setUsername] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [bitcoinJourney, setBitcoinJourney] = useState<BitcoinJourney | null>(null);
  const [predictionData, setPredictionData] = useState<PredictionData | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<Array<{ time: string; value: number }>>([]);
  const [userPredictions, setUserPredictions] = useState<Prediction[]>([]);
  const [wordCloudData, setWordCloudData] = useState<Array<{ text: string; count: number }>>([]);
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activePredictions, setActivePredictions] = useState<number[]>([]);
  const [userRoast, setUserRoast] = useState<{ title: string; content: string } | null>(null);
  
  // Animated handle suggestions
  const [currentHandleIndex, setCurrentHandleIndex] = useState(0);
  const suggestedHandles = ['saylor', 'peterschiff', 'LynAldenContact', '_checkmatey_', 'TimKotzman', 'davidfbailey'];
  
  // Email waitlist state
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [waitlistStatus, setWaitlistStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [waitlistMessage, setWaitlistMessage] = useState('');

  // Rotate through suggested handles
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHandleIndex(prev => (prev + 1) % suggestedHandles.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  // Toggle prediction visibility on chart
  const togglePrediction = (predictionId: number) => {
    setActivePredictions(prev => 
      prev.includes(predictionId)
        ? prev.filter(id => id !== predictionId)
        : [...prev, predictionId]
    );
  };

  // Load chart data and predictions for mobile chart
  const loadChartData = async () => {
    try {
      const response = await fetch('/api/prices');
      if (response.ok) {
        const data = await response.json();
        const formattedData = data.map((item: any) => ({
          time: item.date,
          value: item.close
        }));
        setChartData(formattedData);
      }
    } catch (error) {
      console.error('Failed to load chart data:', error);
    }
  };

  const loadUserPredictions = async (username: string) => {
    try {
      const response = await fetch(`/api/user/${username}/predictions`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.recentPredictions) {
          // Convert backend predictions to frontend Prediction type
          const predictions: Prediction[] = data.recentPredictions.map((p: any, index: number) => ({
            id: index + 1, // Use index as ID since backend doesn't return ID
            price_prediction: p.price_prediction,
            target_time: p.target_time,
            success_status: p.success_status,
            quote: p.quote,
            created_at: p.created_at,
            vote_score: p.vote_score || 0,
            source_url: p.source_url,
            influencer_id: 13, // ejuline's ID
            prediction_type: 'target' // Default type
          }));
          setUserPredictions(predictions);
        }
      }
    } catch (error) {
      console.error('Failed to load user predictions:', error);
    }
  };

  // Load influencer rankings
  const [influencerRankings, setInfluencerRankings] = useState<Array<{
    rank: number;
    name: string;
    username: string;
    profile_image?: string;
    total_predictions: number;
    successful_predictions: number;
    missed_predictions: number;
    pending_predictions: number;
    accuracy_percentage: number;
    completed_predictions: number;
    avg_vote_score: number;
  }>>([]);

  const [worstPerformers, setWorstPerformers] = useState<Array<{
    rank: number;
    name: string;
    username: string;
    profile_image?: string;
    total_predictions: number;
    successful_predictions: number;
    missed_predictions: number;
    pending_predictions: number;
    accuracy_percentage: number;
    completed_predictions: number;
    avg_vote_score: number;
  }>>([]);

  const loadInfluencerRankings = async () => {
    try {
      console.log('Loading leaderboard from API...');
      const response = await fetch('/api/leaderboard/predictions');
      const data = await response.json();
      
      if (data.success && data.leaderboard) {
        console.log('Leaderboard data:', data.leaderboard);
        console.log('Worst performers:', data.worstPerformers);
        setInfluencerRankings(data.leaderboard);
        setWorstPerformers(data.worstPerformers || []);
      } else {
        console.error('Failed to load leaderboard:', data.error);
        setInfluencerRankings([]);
        setWorstPerformers([]);
      }
    } catch (error) {
      console.error('Failed to load influencer rankings:', error);
      setInfluencerRankings([]);
      setWorstPerformers([]);
    }
  };

  // Generate word cloud data from user tweets
  const generateWordCloud = (tweets: Array<{ tweet_text: string }>) => {
    console.log('Word cloud input:', tweets.slice(0, 3)); // Debug first 3 tweets
    
    const commonWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'is', 'are', 'was', 'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'can', 'this', 'that', 'these', 'those', 'i', 'you', 'he', 'she', 'it', 'we', 'they', 'me', 'him', 'her', 'us', 'them', 'my', 'your', 'his', 'her', 'its', 'our', 'their', 'not', 'no', 'yes', 'so', 'if', 'what', 'when', 'where', 'why', 'how', 'all', 'any', 'some', 'one', 'two', 'three', 'from', 'up', 'out', 'many', 'much', 'more', 'most', 'other', 'just', 'only', 'very', 'even', 'back', 'good', 'new', 'first', 'last', 'long', 'great', 'little', 'own', 'old', 'right', 'big', 'high', 'different', 'small', 'large', 'next', 'early', 'young', 'important', 'few', 'public', 'bad', 'same', 'able', 'get', 'got', 'going', 'go', 'like', 'know', 'think', 'see', 'make', 'time', 'way', 'said', 'say', 'look', 'use', 'take', 'come', 'give', 'want', 'work', 'need', 'try', 'feel', 'ask', 'turn', 'move', 'live', 'seem', 'show', 'talk', 'bring', 'sit', 'stand', 'run', 'walk']);
    
    const urlPattern = /https?:\/\/[^\s]+/gi;
    const wordCount: { [key: string]: number } = {};
    
    tweets.forEach(tweet => {
      // Remove URLs first
      const cleanText = tweet.tweet_text.replace(urlPattern, '');
      const words = cleanText
        .toLowerCase()
        .replace(/[^a-zA-Z\s#@]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .split(' ')
        .filter(word => word.length > 3 && !commonWords.has(word) && !word.startsWith('http'));
      
      words.forEach(word => {
        wordCount[word] = (wordCount[word] || 0) + 1;
      });
    });
    
    const sortedWords = Object.entries(wordCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 20)
      .map(([text, count]) => ({ text, count }));
    
    console.log('Word cloud output:', sortedWords.slice(0, 10)); // Debug top 10 words
    setWordCloudData(sortedWords);
  };

  // Story slides configuration - ONLY wrapped content, no welcome
  const getStorySlides = () => {
    if (!analytics) {
      return []; // Empty when no data
    }

    const stats = analytics.stats;
    const topTweets = analytics.topTweets || [];
    
    // Calculate insights
    const bitcoinPercentage = Math.round((stats.bitcoin_tweets / stats.total_tweets) * 100);
    const yearJoined = new Date(stats.first_tweet).getFullYear();
    const yearsActive = new Date().getFullYear() - yearJoined;
    const avgEngagement = Math.round((stats.total_likes / stats.total_tweets));
    
    // Determine personality type
    const getPersonalityType = () => {
      if (bitcoinPercentage >= 80) return "Bitcoin Maximalist";
      if (bitcoinPercentage >= 60) return "Bitcoin Enthusiast";
      if (bitcoinPercentage >= 40) return "Crypto Curious";
      if (bitcoinPercentage >= 20) return "Occasional Bitcoiner";
      return "Traditional Finance";
    };

    const getJourneyPhase = () => {
      if (yearsActive >= 10) return "OG Bitcoiner";
      if (yearsActive >= 5) return "Early Adopter";
      if (yearsActive >= 2) return "Bull Run Veteran";
      return "New to Bitcoin";
    };

    return [
      { id: 'twitter_overview', type: 'twitter_overview', data: { yearJoined, yearsActive, stats, avgEngagement, yearlyProgression: bitcoinJourney?.yearlyProgression } },
      { id: 'personality_insights', type: 'personality_insights', data: { bitcoinPercentage, yearsActive, stats, personalityType: getPersonalityType(), grokAnalysis: analytics.grokAnalysis } },
      { id: 'bitcoin_journey', type: 'bitcoin_journey', data: { firstBitcoinTweet: bitcoinJourney?.firstBitcoinTweet, bitcoinPercentage, yearlyProgression: bitcoinJourney?.yearlyProgression, stats, firstBitcoinTweets: bitcoinJourney?.firstBitcoinTweets } },
      { id: 'your_voice', type: 'your_voice', data: { words: wordCloudData, topTweet: topTweets[0], stats } },
      { id: 'prediction_performance', type: 'prediction_performance', data: { predictionData, username } },
      { id: 'leaderboard_ranking', type: 'leaderboard_ranking', data: { rankings: influencerRankings, worstPerformers, predictionData, username } },
      { id: 'share_results', type: 'share_results', data: { username, bitcoinPercentage, personalityType: getPersonalityType(), predictionData } }
    ];
  };

  const slides = getStorySlides();

  // Email waitlist signup handler
  const submitWaitlist = async () => {
    if (!waitlistEmail.trim() || !waitlistEmail.includes('@')) {
      setWaitlistStatus('error');
      setWaitlistMessage('Please enter a valid email address');
      return;
    }

    setWaitlistStatus('loading');
    try {
      const response = await fetch('/api/auth/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: waitlistEmail.trim(),
          newsletter_opt_in: newsletterOptIn,
          source: 'wrapped_unlock'
        })
      });

      const data = await response.json();
      if (data.success) {
        setWaitlistStatus('success');
        setWaitlistMessage('🎉 You\'re on the list! We\'ll notify you of updates.');
        // Track successful waitlist signup
        trackEvent('waitlist-signup', { source: 'wrapped' });
      } else {
        setWaitlistStatus('error');
        setWaitlistMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setWaitlistStatus('error');
      setWaitlistMessage('Network error. Please try again.');
    }
  };

  const analyzeUser = async () => {
    if (!username.trim()) return;
    
    // Normalize username: remove @ if present (DB stores without @)
    const normalizedUsername = username.trim().replace('@', '').toLowerCase();
    
    // Track wrapped search event
    trackEvent('wrapped-search', { username: normalizedUsername });
    
    console.log('Starting analysis for username:', normalizedUsername);
    setLoading(true);
    setError(null);

    try {
      // Load chart data and predictions in parallel
      console.log('Loading chart data and predictions...');
      const [_, __] = await Promise.all([
        loadChartData(),
        loadUserPredictions(normalizedUsername)
      ]);
      
      // Load influencer rankings
      console.log('Loading influencer rankings...');
      await loadInfluencerRankings();
      
      // Check if user has data and get profile info
      console.log('Checking user status...');
      const statusResponse = await fetch(`/api/user/${normalizedUsername}/status`);
      const statusData = await statusResponse.json();
      console.log('Status data:', statusData);
      
      if (!statusData.success) {
        throw new Error('User not found or data unavailable');
      }
      
      if (!statusData.hasTweets) {
        setError(`No tweet data available for @${normalizedUsername}. This user hasn't been analyzed yet.`);
        return;
      }
      
      // Get profile image from first tweet or user data
      const profileResponse = await fetch(`/api/user/${normalizedUsername}/analytics`);
      const profileData = await profileResponse.json();
      
      if (profileData.success) {
        // Set user profile from analytics data
        if (profileData.profile) {
          setUserProfile({
            profile_image: profileData.profile.profile_image,
            display_name: profileData.profile.name
          });
        } else if (profileData.topTweets && profileData.topTweets.length > 0) {
          // Try to get profile image from tweet data or influencer data
          const topTweet = profileData.topTweets[0];
          setUserProfile({
            profile_image: topTweet.profile_image_url || '/default-avatar.png',
            display_name: username
          });
        }
      }
      
      // Get analytics
      const analyticsData = profileData;
      
      if (!analyticsData.success) {
        throw new Error('Failed to load analytics');
      }
      
      // Get Bitcoin journey data
      const journeyResponse = await fetch(`/api/user/${normalizedUsername}/bitcoin-journey`);
      const journeyData = await journeyResponse.json();
      
      // Get prediction performance data
      const predictionResponse = await fetch(`/api/user/${normalizedUsername}/predictions`);
      const predictionDataResponse = await predictionResponse.json();
      
      // Get user roast
      const roastResponse = await fetch(`/api/user/${normalizedUsername}/roast`);
      const roastData = await roastResponse.json();
      if (roastData.success) {
        setUserRoast(roastData.roast);
      }
      
      setAnalytics(analyticsData);
      setBitcoinJourney(journeyData.success ? journeyData : null);
      setPredictionData(predictionDataResponse.success ? predictionDataResponse : null);
      
      // Convert recentPredictions to Prediction[] format for chart
      if (predictionDataResponse.success && predictionDataResponse.recentPredictions) {
        const formattedPredictions: Prediction[] = predictionDataResponse.recentPredictions.map((pred: any) => ({
          id: pred.id,
          price_target: parseFloat(pred.price_prediction),
          target_time: pred.target_time,
          success_status: pred.success_status,
          quote: pred.quote,
          created_at: pred.created_at,
          vote_score: pred.vote_score,
          source_url: pred.source_url,
          prediction_type: pred.prediction_type || 'target',
          price_min: pred.price_min ? parseFloat(pred.price_min) : undefined,
          price_max: pred.price_max ? parseFloat(pred.price_max) : undefined,
          start_date: pred.start_date || pred.created_at,
          end_date: pred.end_date || pred.target_time,
          influencer: {
            id: 13, // ejuline's ID
            name: pred.influencer_name || normalizedUsername,
            score: 0,
            x_handle: pred.x_handle || normalizedUsername,
            profile_image: pred.profile_image
          }
        }));
        setUserPredictions(formattedPredictions);
        // Initialize all predictions as active
        setActivePredictions(formattedPredictions.map(p => p.id));
      }
      
      // Generate word cloud from ALL Bitcoin tweets for accurate counts
      const bitcoinTweetsResponse = await fetch(`/api/user/${normalizedUsername}/bitcoin-tweets`);
      const bitcoinTweetsData = await bitcoinTweetsResponse.json();
      
      if (bitcoinTweetsData.success && bitcoinTweetsData.bitcoinTweets) {
        console.log(`Generating word cloud from ${bitcoinTweetsData.count} Bitcoin tweets`);
        generateWordCloud(bitcoinTweetsData.bitcoinTweets);
      } else if (journeyData.success && journeyData.topTweets) {
        generateWordCloud(journeyData.topTweets);
      } else if (analyticsData.bitcoinTweets && analyticsData.bitcoinTweets.length > 0) {
        generateWordCloud(analyticsData.bitcoinTweets);
      } else if (analyticsData.success && analyticsData.topTweets) {
        generateWordCloud(analyticsData.topTweets);
      }
      
      console.log('Analysis completed successfully, starting wrapped...');
      
      // Track successful wrapped load
      trackEvent('wrapped-loaded', { 
        username: normalizedUsername,
        tweets: analyticsData.stats?.total_tweets || 0,
        bitcoin_tweets: analyticsData.stats?.bitcoin_tweets || 0
      });
      
      // Data is loaded, useEffect will handle the transition automatically
      
    } catch (err) {
      console.error('Error during analysis:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      
      // Track wrapped error
      trackEvent('wrapped-error', { 
        username: normalizedUsername,
        error: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      console.log('Analysis finished, loading state:', loading);
      setLoading(false);
    }
  };

  // Handle scroll for progress tracking and section detection - Only in wrapped mode
  const handleScroll = () => {
    if (!analytics) return; // Only track scroll in wrapped mode
    
    const container = document.querySelector('.scroll-container');
    if (!container) return;
    
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight - container.clientHeight;
    const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
    
    setScrollProgress(progress);
    
    // Update current section based on scroll position
    const sections = container.querySelectorAll('[style*="scroll-snap-align"]');
    sections.forEach((section, index) => {
      const rect = section.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      
      // Check if section is in the center of viewport
      if (rect.top <= containerRect.height / 2 && rect.bottom >= containerRect.height / 2) {
        setCurrentSection(index);
        setCurrentSlide(index);
      }
    });
  };

  // Touch/swipe navigation - Only in wrapped mode
  // Track scroll position for world-class navigation experience
  React.useEffect(() => {
    if (!analytics) return; // Only setup scroll tracking in wrapped mode
    
    const container = document.querySelector('.scroll-container');
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [analytics]); // Re-bind when analytics loads

  const scrollToSlide = (index: number) => {
    if (!analytics) return; // Only allow scrolling in wrapped mode
    
    const container = document.querySelector('.scroll-container');
    if (!container) return;
    
    const slideHeight = container.clientHeight;
    const targetScrollTop = index * slideHeight;
    
    container.scrollTo({
      top: targetScrollTop,
      behavior: 'smooth'
    });
    
    setCurrentSlide(index);
  };



  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  const shareResults = () => {
    if (!analytics) return;
    
    const bitcoinPercentage = Math.round((analytics.stats.bitcoin_tweets / analytics.stats.total_tweets) * 100);
    const yearJoined = new Date(analytics.stats.first_tweet).getFullYear();
    const yearsActive = new Date().getFullYear() - yearJoined;
    
    const text = `My Bitcoin Wrapped 🧡\n\n📊 ${bitcoinPercentage}% Bitcoin-related tweets\n🗓️ Active since ${yearJoined} (${yearsActive} years)\n💎 ${formatNumber(analytics.stats.total_likes)} total likes\n${predictionData?.hasPredictions ? `🎯 ${predictionData.stats.success_rate}% prediction accuracy\n` : ''}🔗 Get yours at proofofprice.com/btc`;
    
    if (navigator.share) {
      navigator.share({
        title: 'My Bitcoin Wrapped',
        text: text,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(text);
      alert('Results copied to clipboard! 📋');
    }
  };

  const shareToTwitter = () => {
    if (!analytics) return;
    
    // Track share event
    trackEvent('wrapped-share', { 
      username: username,
      type: 'twitter-stats'
    });
    
    const bitcoinPercentage = Math.round((analytics.stats.bitcoin_tweets / analytics.stats.total_tweets) * 100);
    const text = encodeURIComponent(`My Bitcoin Wrapped 🧡\n\n📊 ${bitcoinPercentage}% Bitcoin tweets\n💎 ${formatNumber(analytics.stats.total_likes)} likes\n${predictionData?.hasPredictions ? `🎯 ${predictionData.stats.success_rate}% accuracy\n` : ''}Get yours ⬇️`);
    const url = encodeURIComponent('https://proofofprice.com/btc');
    
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  // Helper function for prediction titles
  const getPredictionTitle = (predictionData: any) => {
    if (!predictionData?.hasPredictions) return null;
    const successRate = predictionData.stats.success_rate || 0;
    const totalPredictions = predictionData.stats.total_predictions || 0;
    
    if (successRate >= 80 && totalPredictions >= 5) return "Prediction Master";
    if (successRate >= 60 && totalPredictions >= 3) return "Market Analyst";
    if (successRate >= 40) return "Price Tracker";
    if (successRate >= 20) return "Learning Trader";
    if (totalPredictions >= 3) return "Bold Predictor";
    return "Market Participant";
  };

  // Bitcoin Personality Zodiac System 
  const getBitcoinPersonality = (stats: any, bitcoinPercentage: number, yearsActive: number, firstBitcoinYear: number) => {
    const currentYear = new Date().getFullYear();
    
    // TRUE OG Status (Bitcoin content from 2009-2011)
    if (firstBitcoinYear <= 2011) {
      return {
        type: "Bitcoin OG 🧙‍♂️",
        description: "You were talking about Bitcoin when it was truly magic internet money. You might have known Hal Finney personally or been on the cryptography mailing list.",
        roast: "You probably have Bitcoin on a hard drive you can't find, and every crypto bro thinks you're Satoshi."
      };
    }
    
    // Early Adopter (2012-2013, pre-mainstream) 
    if (firstBitcoinYear <= 2013) {
      return {
        type: "Bitcoin Early Adopter ⚡",
        description: "You discovered Bitcoin before the mainstream knew it existed. You survived Mt. Gox and probably have some wild stories.",
        roast: "You've been explaining Bitcoin since before it was cool, and you're still explaining it."
      };
    }
    
    // Cycle Veteran (2014-2016)
    if (firstBitcoinYear <= 2016 && yearsActive >= 7) {
      return {
        type: "Cycle Veteran 🔄", 
        description: "You've been through multiple Bitcoin cycles. You know what real bear markets look like and understand the 4-year pattern.",
        roast: "You casually mention surviving the 2018 crash while newcomers panic about 20% dips."
      };
    }
    
    // Cypherpunk (high % + early adoption 2017 era)
    if (bitcoinPercentage >= 60 && firstBitcoinYear <= 2017 && yearsActive >= 6) {
      return {
        type: "Cypherpunk 🕵️‍♂️",
        description: "You understand that Bitcoin is about more than number go up. Privacy, sovereignty, and cryptography are your thing. You probably run your own node.",
        roast: "You're the person who explains why KYC is bad while everyone else is just trying to buy the dip on Coinbase."
      };
    }
    
    // 2017 Bull Runner
    if (firstBitcoinYear >= 2017 && firstBitcoinYear <= 2018) {
      return {
        type: "2017 Bull Runner 🚀",
        description: "You joined during the epic 2017 bull run. You've experienced both euphoria and the crypto winter that followed.",
        roast: "You bought your first Bitcoin when your Uber driver was giving crypto advice, and somehow you're still here."
      };
    }
    
    // Wholecoiner (high % + decent timeline)
    if (bitcoinPercentage >= 70 && yearsActive >= 3) {
      return {
        type: "Wholecoiner 💎",
        description: "You're all-in on Bitcoin. Your timeline is pure orange, you stack sats regularly, and you probably measure everything in Bitcoin terms.",
        roast: "You've definitely calculated how much that coffee costs in satoshis and it hurt your soul."
      };
    }
    
    // Core Dev (very high % + old account + specific terms)
    if (bitcoinPercentage >= 80 && yearsActive >= 8) {
      return {
        type: "Core Dev Energy ⚡",
        description: "Your timeline reads like Bitcoin development updates. You speak in technical terms and probably understand how the sausage is made.",
        roast: "You're the person who corrects others about transaction fees while they're just trying to send $20 to their friend."
      };
    }
    
    // Suitcoiner (moderate % + newer + professional)
    if (bitcoinPercentage >= 30 && bitcoinPercentage <= 60 && firstBitcoinYear >= 2019) {
      return {
        type: "Suitcoiner 👔",
        description: "You discovered Bitcoin through institutional adoption. Michael Saylor is probably your hero. You talk about \"digital gold\" and \"store of value.\"",
        roast: "You bought Bitcoin because your financial advisor finally said it was okay, didn't you?"
      };
    }
    
    // Pleb (moderate % + learning phase)
    if (bitcoinPercentage >= 20 && bitcoinPercentage <= 50) {
      return {
        type: "Bitcoin Pleb 🟠",
        description: "You're on the Bitcoin journey! Not quite all-in yet, but you're learning and stacking. You probably follow Bitcoin X religiously.",
        roast: "You're still in the phase where you check the price every 5 minutes but tell people you're 'in it for the tech.'"
      };
    }
    
    // Paper Bitcoin Summer (low % or new)
    if (bitcoinPercentage < 20 || yearsActive < 2) {
      return {
        type: "Paper Bitcoin Summer 📄",
        description: "You're Bitcoin-curious but not quite orange-pilled yet. Maybe you bought some on PayPal? That's okay, we all start somewhere.",
        roast: "You're probably still trying to figure out if Bitcoin is the same thing as cryptocurrency."
      };
    }
    
    // Nocoiner (very low %)
    if (bitcoinPercentage < 5) {
      return {
        type: "Nocoiner 🤷‍♂️",
        description: "Bitcoin? What Bitcoin? You occasionally tweet about it but you're definitely not convinced yet. Probably waiting for it to \"stabilize.\"",
        roast: "You're the person who says 'Bitcoin is too volatile' while holding dollars that lose 8% buying power per year."
      };
    }
    
    // Default
    return {
      type: "Bitcoin Curious 🤔",
      description: "You're exploring Bitcoin but haven't fully committed yet. You're in the learning phase and that's perfectly fine!",
      roast: "You're still googling 'what is Bitcoin' but you're here, so you're ahead of 99% of people."
    };
  };

  // Check URL parameters on mount (for OAuth redirect or direct URL)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlUser = params.get('user');
    const authenticated = params.get('authenticated');
    const token = params.get('token');
    
    // Store JWT token if provided
    if (token) {
      localStorage.setItem('jwt_token', token);
      console.log('JWT token stored in localStorage');
    }
    
    // Priority: URL path param > query param
    const userToLoad = urlUsername || urlUser;
    
    if (userToLoad) {
      const normalizedUser = userToLoad.replace('@', '');
      setUsername(normalizedUser);
      
      if (authenticated === 'true' || urlUsername) {
        setIsAuthenticated(true);
        // Auto-analyze for authenticated users or direct URL access
        setTimeout(() => {
          analyzeUser();
        }, 500);
      }
    }
  }, [urlUsername]);

  // Load chart data on component mount
  useEffect(() => {
    loadChartData();
  }, []);

  // Inject global styles for perfect edge-to-edge background
  useEffect(() => {
    const originalBodyStyle = {
      margin: document.body.style.margin,
      padding: document.body.style.padding,
      background: document.body.style.background,
      backgroundSize: document.body.style.backgroundSize,
      backgroundRepeat: document.body.style.backgroundRepeat,
      backgroundPosition: document.body.style.backgroundPosition,
      backgroundAttachment: document.body.style.backgroundAttachment
    };
    
    // Apply global background styling
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.background = `
      linear-gradient(135deg, #8B0000 0%, #A52A2A 50%, #8B0000 100%),
      url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"><g transform="translate(50,50)"><path d="M50,50 L50,200 M50,50 L120,50 Q150,50 150,80 Q150,100 130,110 Q160,110 160,140 Q160,170 120,170 L50,170 M50,110 L130,110 M40,40 L60,40 M40,210 L60,210 M40,180 L60,180 M40,20 L60,20" stroke="%23ffffff" stroke-width="8" fill="none" opacity="0.15"/></g><g transform="translate(200,150)"><path d="M50,50 L50,200 M50,50 L120,50 Q150,50 150,80 Q150,100 130,110 Q160,110 160,140 Q160,170 120,170 L50,170 M50,110 L130,110 M40,40 L60,40 M40,210 L60,210 M40,180 L60,180 M40,20 L60,20" stroke="%23ffffff" stroke-width="6" fill="none" opacity="0.12"/></g><g transform="translate(300,250)"><path d="M50,50 L50,200 M50,50 L120,50 Q150,50 150,80 Q150,100 130,110 Q160,110 160,140 Q160,170 120,170 L50,170 M50,110 L130,110 M40,40 L60,40 M40,210 L60,210 M40,180 L60,180 M40,20 L60,20" stroke="%23ffffff" stroke-width="5" fill="none" opacity="0.08"/></g></svg>')
    `;
    document.body.style.backgroundRepeat = 'repeat';
    document.body.style.backgroundRepeat = 'repeat';
    document.body.style.backgroundPosition = '0 0';
    document.body.style.backgroundAttachment = 'fixed';
    
    // Also apply to html element for full coverage
    const originalHtmlStyle = {
      margin: document.documentElement.style.margin,
      padding: document.documentElement.style.padding,
      height: document.documentElement.style.height
    };
    
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    document.documentElement.style.height = '100%';
    
    return () => {
      // Cleanup: restore original styles
      Object.assign(document.body.style, originalBodyStyle);
      Object.assign(document.documentElement.style, originalHtmlStyle);
    };
  }, []);

  // Inject global styles for perfect background
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = `\n      html, body {\n        margin: 0;\n        padding: 0;\n        height: 100%;\n        overflow-x: hidden;\n      }\n    `;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Auto-start wrapped experience when analytics loads
  useEffect(() => {
    if (analytics && !loading) {
      console.log('Analytics loaded, starting wrapped experience...');
      setCurrentSlide(0); // Reset to first slide
      setTimeout(() => {
        scrollToSlide(0); // Go to first wrapped slide
      }, 200);
    }
  }, [analytics, loading]);

  const handleTweetClick = (tweetUrl: string) => {
    if (tweetUrl && tweetUrl !== 'N/A') {
      window.open(tweetUrl, '_blank');
    }
  };

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'welcome':
        // Generate snowflakes for welcome screen
        const welcomeSnowflakes = Array.from({ length: 15 }, (_, i) => ({
          id: i,
          delay: Math.random() * 5,
          duration: 8 + Math.random() * 4, // 8-12 seconds
          left: Math.random() * 100,
          size: 16 + Math.random() * 12 // 16-28px
        }));
        
        return (
          <SlideContainer>
            {/* Falling Bitcoin Snowflakes */}
            <SnowflakeContainer>
              {welcomeSnowflakes.map(flake => (
                <Snowflake 
                  key={flake.id}
                  delay={flake.delay}
                  duration={flake.duration}
                  left={flake.left}
                  size={flake.size}
                >
                  ₿
                </Snowflake>
              ))}
            </SnowflakeContainer>
            
            {/* Compact Header with subtle animations */}
            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
              <BigTitle style={{ 
                fontSize: 'clamp(1.8rem, 8vw, 2.5rem)', 
                lineHeight: '1.1', 
                marginBottom: '5px',
                background: loading ? 'linear-gradient(45deg, #f7931a, #ff6b00)' : 'inherit',
                WebkitBackgroundClip: loading ? 'text' : 'inherit',
                WebkitTextFillColor: loading ? 'transparent' : 'inherit',
                transition: 'all 0.3s ease'
              }}>
                ProofOfPrice<br/>Wrapped
              </BigTitle>
              <SubTitle style={{ fontSize: 'clamp(1rem, 4vw, 1.2rem)', opacity: '0.8' }}>2025</SubTitle>
              <StoryText style={{ 
                fontSize: 'clamp(0.85rem, 3.5vw, 1rem)', 
                margin: '10px 0 0 0', 
                opacity: loading ? '0.6' : '0.9',
                transition: 'opacity 0.3s ease'
              }}>
                {loading ? 'Analyzing your Bitcoin journey...' : 'Discover your Bitcoin journey through your tweets'}
              </StoryText>
            </div>
            
            {!loading && (
              <>
                {/* Compact Input Section */}
                <div style={{ marginBottom: '15px' }}>
                  <div 
                    onClick={() => {
                      setUsername(suggestedHandles[currentHandleIndex]);
                      setError(null);
                    }}
                    style={{ 
                      fontSize: '0.75rem', 
                      color: '#f7931a', 
                      marginBottom: '8px', 
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'opacity 0.3s ease'
                    }}
                  >
                    ✨ Try{' '}
                    <span style={{ 
                      fontWeight: '600',
                      background: 'rgba(247, 147, 26, 0.2)',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      transition: 'all 0.3s ease'
                    }}>
                      @{suggestedHandles[currentHandleIndex]}
                    </span>
                    {' '}or any X handle
                  </div>
                  <InputContainer style={{ gap: '10px' }}>
                    <UsernameInput
                      type="text"
                      inputMode="text"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      enterKeyHint="go"
                      placeholder="@username"
                      value={username ? `@${username}` : ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setUsername(value.startsWith('@') ? value.slice(1) : value);
                        setError(null); // Clear errors on input
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && analyzeUser()}
                      onClick={(e) => (e.target as HTMLInputElement).focus()}
                      style={{ 
                        fontSize: 'clamp(16px, 4vw, 18px)', 
                        padding: '12px 16px',
                        transition: 'all 0.2s ease',
                        borderColor: username ? '#f7931a' : 'rgba(255, 255, 255, 0.2)'
                      }}
                    />
                    <AnalyzeButton 
                      onClick={analyzeUser} 
                      disabled={loading || !username.trim()}
                      style={{ 
                        fontSize: 'clamp(0.85rem, 3.5vw, 0.95rem)', 
                        padding: '12px 20px',
                        minHeight: '44px',
                        opacity: !username.trim() ? 0.6 : 1,
                        transform: username.trim() ? 'scale(1.02)' : 'scale(1)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Create My Wrapped
                    </AnalyzeButton>
                  </InputContainer>
                </div>
              </>
            )}
            
            {analytics && !loading && currentSlide === 0 && (
              <div style={{ textAlign: 'center', margin: '20px 0' }}>
                <StoryText style={{ 
                  fontSize: 'clamp(0.9rem, 3.5vw, 1rem)', 
                  color: '#f7931a',
                  fontWeight: '500'
                }}>
                  🎉 Analysis complete! Starting your journey...
                </StoryText>
              </div>
            )}
            
            {loading && (
              <ProgressContainer style={{ margin: '30px 0' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'conic-gradient(from 0deg, #f7931a, #ff6b00, #f7931a)',
                  animation: 'spin 2s linear infinite',
                  margin: '0 auto 20px auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: '#000'
                  }} />
                </div>
                <ProgressText style={{ fontSize: '0.9rem', textAlign: 'center' }}>
                  {loading ? (
                    <>
                      Analyzing @{username}'s Bitcoin journey...<br/>
                      <span style={{ fontSize: '0.75rem', opacity: '0.7' }}>This may take a moment ✨</span>
                    </>
                  ) : 'Ready to explore!'}
                </ProgressText>
                
                {/* Progress bar */}
                <div style={{
                  width: '100%',
                  height: '3px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '2px',
                  marginTop: '20px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    background: 'linear-gradient(90deg, #f7931a, #ff6b00)',
                    borderRadius: '2px',
                    width: '100%',
                    animation: 'loading-progress 3s ease-in-out infinite'
                  }} />
                </div>
              </ProgressContainer>
            )}
            
            {error && (
              <InsightCard style={{ 
                borderColor: error.includes('hasn\'t been analyzed') ? '#f7931a' : '#ff6b6b', 
                background: error.includes('hasn\'t been analyzed') ? 'rgba(247, 147, 26, 0.08)' : 'rgba(255, 107, 107, 0.1)',
                margin: '15px 0',
                padding: '20px',
                borderRadius: '16px'
              }}>
                {error.includes('hasn\'t been analyzed') ? (
                  <>
                    {/* User not found - upgrade prompt */}
                    <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                      <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🚀</div>
                      <StoryText style={{ 
                        color: '#fff', 
                        fontSize: '1rem', 
                        fontWeight: '600',
                        marginBottom: '8px'
                      }}>
                        Get Your Bitcoin Wrapped!
                      </StoryText>
                      <StoryText style={{ color: '#b0b0b0', fontSize: '0.85rem', lineHeight: '1.5' }}>
                        @{username} hasn't been analyzed yet.
                      </StoryText>
                    </div>
                    
                    {/* What you get */}
                    <div style={{ 
                      background: 'rgba(0, 0, 0, 0.2)', 
                      borderRadius: '12px', 
                      padding: '14px',
                      marginBottom: '16px'
                    }}>
                      <StoryText style={{ color: '#f7931a', fontSize: '0.8rem', fontWeight: '600', marginBottom: '10px' }}>
                        ⚡ What you'll get:
                      </StoryText>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <StoryText style={{ color: '#ccc', fontSize: '0.8rem', margin: 0 }}>
                          📊 AI analysis of your Bitcoin tweets
                        </StoryText>
                        <StoryText style={{ color: '#ccc', fontSize: '0.8rem', margin: 0 }}>
                          📈 Personal dashboard with your data
                        </StoryText>
                        <StoryText style={{ color: '#ccc', fontSize: '0.8rem', margin: 0 }}>
                          🎯 Track your Bitcoin predictions
                        </StoryText>
                        <StoryText style={{ color: '#ccc', fontSize: '0.8rem', margin: 0 }}>
                          🏆 See how you rank vs other influencers
                        </StoryText>
                      </div>
                    </div>
                    
                    {/* CTA Button */}
                    <AnalyzeButton 
                      onClick={() => {
                        window.location.href = '/api/auth/x/login?returnTo=/pricing';
                      }}
                      style={{ 
                        background: 'linear-gradient(45deg, #f7931a, #ff6b00)', 
                        fontSize: '0.95rem',
                        padding: '14px 20px',
                        width: '100%',
                        fontWeight: '600',
                        boxShadow: '0 4px 15px rgba(247, 147, 26, 0.3)'
                      }}
                    >
                      ⚡ Sign In & Get Started
                    </AnalyzeButton>
                    
                    <StoryText style={{ 
                      color: '#888', 
                      fontSize: '0.7rem', 
                      textAlign: 'center', 
                      marginTop: '12px',
                      fontStyle: 'italic'
                    }}>
                      Pay with Lightning ⚡ · Instant delivery
                    </StoryText>
                    
                    {/* Divider */}
                    <div style={{ 
                      borderTop: '1px solid rgba(255,255,255,0.1)', 
                      margin: '16px 0',
                      paddingTop: '12px'
                    }}>
                      <StoryText style={{ color: '#888', fontSize: '0.75rem', textAlign: 'center', marginBottom: '8px' }}>
                        Or try a demo with existing users:
                      </StoryText>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
                        {suggestedHandles.slice(0, 4).map(handle => (
                          <PersonalityBadge
                            key={handle}
                            onClick={() => {
                              setUsername(handle);
                              setError(null);
                            }}
                            style={{ 
                              cursor: 'pointer',
                              fontSize: '0.7rem',
                              padding: '4px 10px',
                              background: 'rgba(29, 161, 242, 0.15)',
                              border: '1px solid rgba(29, 161, 242, 0.3)'
                            }}
                          >
                            @{handle}
                          </PersonalityBadge>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <StoryText style={{ color: '#ff6b6b', fontSize: '0.85rem' }}>
                    {error}
                  </StoryText>
                )}
              </InsightCard>
            )}
            
            {/* Compact Branding */}
            <InsightCard style={{ 
              marginTop: '20px',
              padding: '12px',
              background: 'rgba(247, 147, 26, 0.05)',
              border: '1px solid rgba(247, 147, 26, 0.2)'
            }}>
              <LogoText style={{ textAlign: 'center', fontSize: '0.85rem', margin: '0 0 5px 0' }}>
                Powered by ProofOfPrice.com
              </LogoText>
              <StoryText style={{ fontSize: '0.7rem', color: '#b0b0b0', margin: '0', textAlign: 'center' }}>
                Track Bitcoin predictions & analyze crypto X
              </StoryText>
            </InsightCard>
          </SlideContainer>
        );

      case 'twitter_overview':
        // Get yearly progression data and filter to relevant years (2017-2025)
        const yearlyData = slide.data.yearlyProgression || [];
        const bitcoinPercentage = Math.round((slide.data.stats.bitcoin_tweets / slide.data.stats.total_tweets) * 100);
        
        // Filter years from 2017 onwards and convert to proper format
        const chartYears = yearlyData
          .filter(y => {
            const year = new Date(y.year).getFullYear();
            return year >= 2017 && year <= 2025;
          })
          .map(y => ({
            year: new Date(y.year).getFullYear(),
            total: parseInt(y.total_tweets_year) || 0,
            bitcoin: parseInt(y.bitcoin_tweets_year) || 0
          }));
        
        // Calculate max value for scaling
        const maxTweets = Math.max(...chartYears.map(y => y.total));
        
        return (
          <SlideContainer>
            <SubTitle>@{username}</SubTitle>
            <BigTitle style={{ fontSize: 'clamp(1.5rem, 6vw, 2.2rem)', marginBottom: '20px' }}>
              Your X<br/>Journey
            </BigTitle>
            
            {/* Key Stats Grid - Only Total Tweets and Avg Likes */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '15px', 
              marginBottom: '25px',
              maxWidth: '350px',
              width: '100%'
            }}>
              <InsightCard style={{ padding: '15px', textAlign: 'center' }}>
                <StatNumber style={{ fontSize: '1.8rem', margin: '0 0 5px 0' }}>{formatNumber(slide.data.stats.total_tweets)}</StatNumber>
                <StatLabel style={{ fontSize: '0.8rem', margin: '0' }}>Total Tweets</StatLabel>
              </InsightCard>
              
              <InsightCard style={{ padding: '15px', textAlign: 'center' }}>
                <StatNumber style={{ fontSize: '1.8rem', margin: '0 0 5px 0' }}>{formatNumber(slide.data.avgEngagement)}</StatNumber>
                <StatLabel style={{ fontSize: '0.8rem', margin: '0' }}>Avg Likes</StatLabel>
              </InsightCard>
            </div>
            
            {/* Year-by-Year Bar Chart - Compact */}
            {chartYears.length > 0 && (
              <div style={{ width: '100%', maxWidth: '350px', marginBottom: '20px' }}>
                <StoryText style={{ 
                  fontSize: '0.9rem', 
                  color: '#f7931a', 
                  textAlign: 'center',
                  marginBottom: '12px',
                  fontWeight: '600'
                }}>
                  📊 Bitcoin Tweets Over Time
                </StoryText>
                
                <div style={{
                  background: 'rgba(0, 0, 0, 0.4)',
                  borderRadius: '12px',
                  padding: '15px 10px 12px 10px',
                  border: '1px solid rgba(247, 147, 26, 0.2)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-end', 
                    justifyContent: 'space-between',
                    gap: '4px',
                    minHeight: '100px',
                    paddingTop: '40px'
                  }}>
                    {chartYears.map((yearData, index) => {
                      const totalHeight = (yearData.total / maxTweets) * 80;
                      const bitcoinHeight = (yearData.bitcoin / maxTweets) * 80;
                      
                      return (
                        <div key={yearData.year} style={{ 
                          flex: '1',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          minWidth: '28px',
                          maxWidth: '38px'
                        }}>
                          {/* Numbers above bars */}
                          <div style={{
                            position: 'relative',
                            width: '100%',
                            height: '35px',
                            marginBottom: '2px'
                          }}>
                            {/* Total tweet count (gray) */}
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              fontSize: '0.6rem',
                              fontWeight: '600',
                              color: '#999',
                              whiteSpace: 'nowrap'
                            }}>
                              {yearData.total}
                            </div>
                            
                            {/* Bitcoin tweet count (orange) - aligned below total */}
                            {yearData.bitcoin > 0 && (
                              <div style={{
                                position: 'absolute',
                                top: '15px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                fontSize: '0.65rem',
                                fontWeight: '700',
                                color: '#f7931a',
                                whiteSpace: 'nowrap'
                              }}>
                                {yearData.bitcoin}
                              </div>
                            )}
                          </div>
                          
                          {/* Bar container */}
                          <div style={{ 
                            position: 'relative',
                            width: '100%',
                            height: '80px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-end'
                          }}>
                            {/* Total tweets bar (gray background) */}
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              width: '14px',
                              height: `${totalHeight}px`,
                              background: 'linear-gradient(to top, rgba(120, 120, 120, 0.6), rgba(180, 180, 180, 0.8))',
                              borderRadius: '2px 2px 0 0',
                              border: '1px solid rgba(180, 180, 180, 0.5)'
                            }} />
                            
                            {/* Bitcoin tweets bar (orange overlay) */}
                            {yearData.bitcoin > 0 && (
                              <div style={{
                                position: 'absolute',
                                bottom: 0,
                                width: '14px',
                                height: `${bitcoinHeight}px`,
                                background: 'linear-gradient(to top, #d84315, #f7931a, #ffb74d)',
                                borderRadius: '2px 2px 0 0',
                                border: '1px solid rgba(247, 147, 26, 0.9)',
                                boxShadow: '0 2px 4px rgba(247, 147, 26, 0.3)'
                              }} />
                            )}
                          </div>
                          
                          {/* Year label */}
                          <div style={{ 
                            fontSize: '0.6rem', 
                            color: '#888',
                            marginTop: '4px',
                            fontWeight: '500'
                          }}>
                            '{String(yearData.year).slice(-2)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Legend */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    gap: '12px',
                    marginTop: '10px',
                    fontSize: '0.65rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ 
                        width: '10px', 
                        height: '10px', 
                        background: 'linear-gradient(to top, rgba(120, 120, 120, 0.6), rgba(180, 180, 180, 0.8))',
                        borderRadius: '2px',
                        border: '1px solid rgba(180, 180, 180, 0.5)'
                      }} />
                      <span style={{ color: '#aaa' }}>Total</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ 
                        width: '10px', 
                        height: '10px', 
                        background: 'linear-gradient(to top, #d84315, #f7931a)',
                        borderRadius: '2px',
                        border: '1px solid rgba(247, 147, 26, 0.9)'
                      }} />
                      <span style={{ color: '#f7931a' }}>Bitcoin</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Single year addiction message */}
            {chartYears.length === 1 && (
              <InsightCard style={{ 
                background: 'linear-gradient(135deg, rgba(247, 147, 26, 0.15), rgba(255, 193, 7, 0.1))', 
                border: '2px solid rgba(247, 147, 26, 0.4)',
                marginBottom: '15px'
              }}>
                <StoryText style={{ textAlign: 'center', fontSize: '1rem', fontWeight: '600', color: '#ffb74d' }}>
                  🎄 We analyzed {formatNumber(slide.data.stats.total_tweets)} tweets and they're all from {chartYears[0].year}!<br/>
                  <span style={{ fontSize: '0.9rem', color: '#e0e0e0' }}>Lay off the phone for a while and enjoy the Christmas days! 😅</span>
                </StoryText>
              </InsightCard>
            )}
            
            <InsightCard style={{ background: 'rgba(247, 147, 26, 0.1)', border: '1px solid rgba(247, 147, 26, 0.3)' }}>
              <StoryText style={{ textAlign: 'center', fontSize: '0.9rem' }}>
                We analyzed <strong>{formatNumber(slide.data.stats.total_tweets)}</strong> tweets from your archive to track your Bitcoin journey 🧡
              </StoryText>
            </InsightCard>
          </SlideContainer>
        );

      case 'your_voice':
        const voiceData = slide.data;
        const topTweet = voiceData.topTweet;
        
        return (
          <SlideContainer>
            <SubTitle>💬 Your Voice</SubTitle>
            <BigTitle style={{ fontSize: 'clamp(1.4rem, 5vw, 1.8rem)', marginBottom: '20px' }}>Most Used Words</BigTitle>
            
            {/* Compact Word Cloud */}
            <div style={{
              width: '100%',
              maxWidth: '350px',
              minHeight: '150px',
              background: 'rgba(255, 255, 255, 0.02)',
              borderRadius: '12px',
              padding: '15px',
              marginBottom: '20px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                minHeight: '120px'
              }}>
                {voiceData.words.slice(0, 15).map((word, index) => (
                  <span
                    key={word.text}
                    title={`Used ${word.count} times`}
                    style={{
                      fontSize: `${Math.max(0.7, Math.min(1.4, 0.7 + (word.count / Math.max(...voiceData.words.map(w => w.count))) * 0.7))}rem`,
                      color: index < 3 ? '#f7931a' : index < 8 ? '#ff6b00' : 'rgba(255, 255, 255, 0.8)',
                      fontWeight: index < 5 ? '600' : '400',
                      padding: '4px 8px',
                      background: index < 3 ? 'rgba(247, 147, 26, 0.1)' : 'transparent',
                      borderRadius: '6px',
                      border: index < 3 ? '1px solid rgba(247, 147, 26, 0.3)' : 'none',
                      lineHeight: '1.2',
                      cursor: 'default',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {word.text}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Top Tweet */}
            {topTweet && (
              <>
                <SubTitle style={{ fontSize: '1.1rem', marginBottom: '12px', color: '#f7931a' }}>🏆 Your Top Tweet</SubTitle>
                <div style={{
                  width: '100%',
                  maxWidth: '380px',
                  margin: '0 auto 15px',
                  background: 'rgba(20, 23, 26, 0.95)',
                  border: '1px solid rgba(56, 68, 77, 0.8)',
                  borderRadius: '16px',
                  padding: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => handleTweetClick(topTweet.tweet_url)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(25, 28, 31, 0.98)';
                  e.currentTarget.style.borderColor = 'rgba(247, 147, 26, 0.4)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(247, 147, 26, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(20, 23, 26, 0.95)';
                  e.currentTarget.style.borderColor = 'rgba(56, 68, 77, 0.8)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                >
                  <TweetHeader>
                    <TweetAvatar profileImage={userProfile?.profile_image}>
                      {!userProfile?.profile_image && username.charAt(0).toUpperCase()}
                    </TweetAvatar>
                    <TweetUserInfo>
                      <TweetDisplayName>{userProfile?.display_name || username}</TweetDisplayName>
                      <TweetHandle>@{username}</TweetHandle>
                    </TweetUserInfo>
                    <div style={{
                      marginLeft: 'auto',
                      fontSize: '0.7rem',
                      fontWeight: '700',
                      color: '#f91880',
                      background: 'rgba(249, 24, 128, 0.15)',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      border: '1px solid rgba(249, 24, 128, 0.4)'
                    }}>
                      🏆 TOP
                    </div>
                  </TweetHeader>
                  
                  {(() => {
                    // Extract image URL if present
                    const imageUrlMatch = topTweet.tweet_text.match(/(https:\/\/t\.co\/[a-zA-Z0-9]+|pic\.twitter\.com\/[a-zA-Z0-9]+)/);
                    const hasImage = imageUrlMatch !== null;
                    const textWithoutUrl = topTweet.tweet_text.replace(/(https:\/\/t\.co\/[a-zA-Z0-9]+|pic\.twitter\.com\/[a-zA-Z0-9]+)/g, '').trim();
                    
                    // Extract tweet ID from URL for embedding
                    const tweetIdMatch = topTweet.tweet_url.match(/status\/(\d+)/);
                    const tweetId = tweetIdMatch ? tweetIdMatch[1] : null;
                    
                    return (
                      <>
                        {textWithoutUrl && (
                          <TweetText style={{ 
                            fontSize: '0.95rem',
                            lineHeight: '1.5',
                            marginBottom: hasImage ? '12px' : '12px'
                          }}>
                            {textWithoutUrl}
                          </TweetText>
                        )}
                        
                        {hasImage && tweetId && (
                          <div style={{
                            width: '100%',
                            marginBottom: '12px',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            border: '1px solid rgba(56, 68, 77, 0.5)',
                            background: '#000',
                            position: 'relative'
                          }}>
                            <img 
                              src={`https://syndication.twitter.com/tweet-result?id=${tweetId}&lang=en&token=placeholder`}
                              alt="Tweet preview"
                              style={{
                                width: '100%',
                                height: 'auto',
                                display: 'block'
                              }}
                              onError={(e) => {
                                // Fallback to placeholder if image fails to load
                                e.currentTarget.style.display = 'none';
                                const placeholder = e.currentTarget.parentElement?.querySelector('.placeholder');
                                if (placeholder) (placeholder as HTMLElement).style.display = 'flex';
                              }}
                            />
                            <div 
                              className="placeholder"
                              style={{
                                width: '100%',
                                aspectRatio: '16/9',
                                display: 'none',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: '8px',
                                background: 'rgba(30, 30, 30, 0.8)'
                              }}
                            >
                              <div style={{ fontSize: '3rem', opacity: 0.3 }}>🖼️</div>
                              <div style={{
                                fontSize: '0.75rem',
                                color: '#aaa',
                                textAlign: 'center',
                                padding: '0 16px'
                              }}>
                                📸 Click to view full tweet with image
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                  
                  <TweetStats>
                    <span className="likes">
                      ❤️ {formatNumber(topTweet.like_count)}
                    </span>
                    <span style={{ color: '#8b949e' }}>
                      📅 {new Date(topTweet.tweet_created_at).toLocaleDateString('en-US', { 
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  </TweetStats>
                </div>
              </>
            )}
          </SlideContainer>
        );
        
      case 'bitcoin_journey':
        const journeyData = slide.data;
        const firstBitcoinTweets = slide.data.firstBitcoinTweets || [];
        
        return (
          <SlideContainer>
            <SubTitle style={{ marginBottom: '8px' }}>🎯 Your Bitcoin Journey</SubTitle>
            <BigTitle style={{ fontSize: 'clamp(1.3rem, 5vw, 1.7rem)', marginBottom: '15px', lineHeight: '1.2' }}>
              First Tweets Mentioning<br />Bitcoin We've Found
            </BigTitle>
            
            {firstBitcoinTweets.length > 0 ? (
              <div style={{ 
                width: '100%',
                maxWidth: '380px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {firstBitcoinTweets.map((tweet, index) => (
                  <TweetCard 
                    key={index}
                    onClick={() => handleTweetClick(tweet.tweet_url)}
                    style={{ 
                      width: '100%',
                      margin: 0,
                      cursor: 'pointer'
                    }}
                  >
                    <TweetHeader>
                      <TweetAvatar profileImage={userProfile?.profile_image}>
                        {!userProfile?.profile_image && username.charAt(0).toUpperCase()}
                      </TweetAvatar>
                      <TweetUserInfo>
                        <TweetDisplayName>{userProfile?.display_name || username}</TweetDisplayName>
                        <TweetHandle>@{username}</TweetHandle>
                      </TweetUserInfo>
                      <div style={{
                        marginLeft: 'auto',
                        fontSize: '0.7rem',
                        fontWeight: '700',
                        color: '#f7931a',
                        background: 'rgba(247, 147, 26, 0.2)',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        border: '1px solid rgba(247, 147, 26, 0.4)'
                      }}>
                        #{index + 1}
                      </div>
                    </TweetHeader>
                    
                    <TweetText style={{ 
                      fontSize: '0.9rem',
                      lineHeight: '1.4',
                      marginBottom: '12px',
                      wordBreak: 'break-word'
                    }}>
                      {tweet.tweet_text}
                    </TweetText>
                    
                    <TweetStats>
                      <span style={{ color: '#8b949e' }}>
                        📅 {new Date(tweet.tweet_created_at).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                      {tweet.like_count > 0 && (
                        <span className="likes">
                          ❤️ {formatNumber(tweet.like_count)}
                        </span>
                      )}
                    </TweetStats>
                  </TweetCard>
                ))}
              </div>
            ) : (
              <InsightCard>
                <StoryText style={{ textAlign: 'center', fontSize: '0.9rem' }}>
                  No Bitcoin tweets found in your archive yet 🔍
                </StoryText>
              </InsightCard>
            )}
            
            <StoryText style={{ fontSize: '0.75rem', color: '#fff', textAlign: 'center', marginTop: '15px' }}>
              Tap any tweet to view on X 🐦
            </StoryText>
          </SlideContainer>
        );

      case 'prediction_performance':
        const predData = slide.data.predictionData;
        const hasPredictions = predData && predData.hasPredictions && predData.stats && predData.stats.total_predictions > 0;
        
        return (
          <SlideContainer>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px', marginTop: '10px' }}>
              <img 
                src="/POP_new_logopng.png" 
                alt="ProofOfPrice" 
                style={{ 
                  width: '28px', 
                  height: '28px', 
                  marginRight: '10px',
                  filter: 'brightness(1.1)'
                }} 
              />
              <SubTitle style={{ margin: '0', fontSize: '1rem' }}>Price Predictions</SubTitle>
            </div>
            <BigTitle style={{ fontSize: 'clamp(1.2rem, 5vw, 1.5rem)', marginBottom: '20px', lineHeight: '1.2' }}>
              Your Track Record
            </BigTitle>
            
            {hasPredictions ? (
              <>
                {/* Stats Grid - More Compact */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(3, 1fr)', 
                  gap: '8px', 
                  marginBottom: '0px',
                  maxWidth: '320px',
                  width: '100%'
                }}>
                  <InsightCard style={{ padding: '10px', textAlign: 'center' }}>
                    <StatNumber style={{ fontSize: '1.3rem', color: '#00ff88', margin: '0 0 3px 0' }}>
                      {predData.stats.successful_predictions || 0}
                    </StatNumber>
                    <StatLabel style={{ fontSize: '0.65rem', margin: '0' }}>✅ Hit</StatLabel>
                  </InsightCard>
                  
                  <InsightCard style={{ padding: '10px', textAlign: 'center' }}>
                    <StatNumber style={{ fontSize: '1.3rem', color: '#ff4444', margin: '0 0 3px 0' }}>
                      {predData.stats.missed_predictions || 0}
                    </StatNumber>
                    <StatLabel style={{ fontSize: '0.65rem', margin: '0' }}>❌ Miss</StatLabel>
                  </InsightCard>
                  
                  <InsightCard style={{ padding: '10px', textAlign: 'center' }}>
                    <StatNumber style={{ fontSize: '1.3rem', color: '#f7931a', margin: '0 0 3px 0' }}>
                      {predData.stats.pending_predictions || 0}
                    </StatNumber>
                    <StatLabel style={{ fontSize: '0.65rem', margin: '0' }}>⏳ Pending</StatLabel>
                  </InsightCard>
                </div>
                
                {/* Mobile Chart with User Predictions */}
                {userPredictions.length > 0 && chartData.length > 0 && (
                  <div style={{ 
                    width: '100%', 
                    maxWidth: '360px',
                    marginBottom: '25px',
                    marginTop: '-18px'
                  }}>
                    <StoryText style={{ 
                      fontSize: '0.8rem', 
                      color: '#f7931a', 
                      textAlign: 'center',
                      marginBottom: '8px'
                    }}>
                      📊 Your Predictions on Chart
                    </StoryText>
                    
                    {/* Chart Container - Larger for Better Visibility */}
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.4)',
                      borderRadius: '12px',
                      padding: '10px 10px 30px 10px',
                      border: '1px solid rgba(247, 147, 26, 0.2)',
                      marginBottom: '10px',
                      height: '290px',
                      overflow: 'visible',
                      position: 'relative'
                    }}>
                      <div style={{ height: '100%', width: '100%', position: 'relative' }}>
                        <MobileChartWrapper 
                          predictions={userPredictions}
                          activePredictions={activePredictions}
                          chartData={chartData}
                          isDarkMode={true}
                          theme={darkTheme}
                        />
                      </div>
                    </div>
                    
                    {/* Prediction Cards - Narrower & Darker */}
                    <div style={{
                      marginTop: '10px',
                      width: '100%',
                      maxWidth: '100%'
                    }}>
                      <StoryText style={{ 
                        fontSize: '0.7rem', 
                        color: '#fff', 
                        textAlign: 'center',
                        marginBottom: '6px'
                      }}>
                        Tap to toggle predictions 👇
                      </StoryText>
                      <div style={{ 
                        maxHeight: 'none',
                        overflow: 'visible',
                        width: '100%'
                      }}>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                          width: '100%',
                          maxWidth: '100%'
                        }}>
                          {userPredictions.filter(p => p.prediction_type !== 'sentiment').map((prediction) => (
                            <div
                              key={prediction.id}
                              onClick={() => togglePrediction(prediction.id)}
                              style={{
                                background: activePredictions.includes(prediction.id)
                                  ? 'rgba(247, 147, 26, 0.2)'
                                  : 'rgba(30, 30, 30, 0.7)',
                                borderLeft: `4px solid ${
                                  prediction.success_status === 'success' ? '#4CAF50' :
                                  prediction.success_status === 'miss' ? '#F44336' :
                                  '#FF9800'
                                }`,
                                borderRadius: '8px',
                                padding: '8px 10px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                width: '100%',
                                maxWidth: '100%',
                                boxSizing: 'border-box',
                                boxShadow: activePredictions.includes(prediction.id)
                                  ? '0 2px 8px rgba(247, 147, 26, 0.3)'
                                  : '0 1px 3px rgba(0, 0, 0, 0.2)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 3px 10px rgba(247, 147, 26, 0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = activePredictions.includes(prediction.id)
                                  ? '0 2px 8px rgba(247, 147, 26, 0.3)'
                                  : '0 1px 3px rgba(0, 0, 0, 0.2)';
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                <div style={{ 
                                  fontSize: '0.75rem', 
                                  fontWeight: '600',
                                  color: '#e0e0e0'
                                }}>
                                  {prediction.influencer?.name || 'Ed Juline'}
                                </div>
                                <div style={{
                                  fontSize: '0.65rem',
                                  fontWeight: '600',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  background: prediction.success_status === 'success' ? 'rgba(76, 175, 80, 0.25)' :
                                              prediction.success_status === 'miss' ? 'rgba(244, 67, 54, 0.25)' :
                                              'rgba(255, 152, 0, 0.25)',
                                  color: prediction.success_status === 'success' ? '#4CAF50' :
                                         prediction.success_status === 'miss' ? '#F44336' :
                                         '#FF9800'
                                }}>
                                  {prediction.success_status === 'success' ? '✅' :
                                   prediction.success_status === 'miss' ? '❌' :
                                   '⏳'}
                                </div>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ 
                                  fontSize: '0.85rem', 
                                  fontWeight: '700',
                                  color: '#f7931a'
                                }}>
                                  ${(prediction.price_target || 0).toLocaleString()}
                                </div>
                                <div style={{
                                  fontSize: '0.65rem',
                                  color: '#999'
                                }}>
                                  {new Date(prediction.target_time || '').toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Success Rate Display - Compact */}
                {predData.stats.success_rate > 0 && (
                  <div style={{ marginTop: '12px', textAlign: 'center' }}>
                    <StatNumber style={{ 
                      fontSize: 'clamp(1.8rem, 7vw, 2.2rem)',
                      background: 'linear-gradient(45deg, #f7931a, #ff6b00)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      margin: '0 0 3px 0'
                    }}>
                      {predData.stats.success_rate}%
                    </StatNumber>
                    <StatLabel style={{ fontSize: '0.75rem', margin: '0', color: '#fff' }}>Accuracy Rate</StatLabel>
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ marginBottom: '25px', textAlign: 'center' }}>
                  <StatNumber style={{ 
                    fontSize: 'clamp(2rem, 10vw, 3rem)',
                    color: '#888',
                    margin: '0 0 5px 0'
                  }}>
                    0
                  </StatNumber>
                  <StatLabel style={{ fontSize: '0.9rem', margin: '0' }}>Tracked Predictions</StatLabel>
                </div>
                
                <InsightCard style={{ background: 'rgba(247, 147, 26, 0.1)', border: '1px solid rgba(247, 147, 26, 0.3)' }}>
                  <StoryText style={{ textAlign: 'center', fontSize: '0.9rem', marginBottom: '15px' }}>
                    📊 We didn't find any Bitcoin price predictions from @{slide.data.username} yet.
                  </StoryText>
                  <StoryText style={{ textAlign: 'center', fontSize: '0.8rem', color: '#888' }}>
                    Join ProofOfPrice.com to track your future calls! 🎯
                  </StoryText>
                </InsightCard>
                
                {/* CTA Button */}
                <SubmitButton 
                  onClick={() => {
                    setShowSubmissionForm(true);
                    setCurrentSlide(0); // Reset to submission form
                  }}
                  style={{ 
                    marginTop: '20px',
                    fontSize: '0.9rem',
                    padding: '12px 24px'
                  }}
                >
                  📊 Submit a Bitcoin Price Prediction
                </SubmitButton>
              </>
            )}
          </SlideContainer>
        );
        const bitcoinData = slide.data;
        const firstTweet = bitcoinData.firstBitcoinTweet;
        
        return (
          <SlideContainer>
            <SubTitle>🧡 Bitcoin Journey</SubTitle>
            <BigTitle style={{ fontSize: 'clamp(1.4rem, 5vw, 1.8rem)', marginBottom: '20px' }}>
              Down the Rabbit Hole
            </BigTitle>
            
            {/* Bitcoin Percentage */}
            <div style={{ marginBottom: '25px', textAlign: 'center' }}>
              <StatNumber style={{ 
                fontSize: 'clamp(2.5rem, 12vw, 4rem)',
                background: 'linear-gradient(45deg, #f7931a, #ff6b00)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                margin: '0 0 5px 0'
              }}>
                {bitcoinData.bitcoinPercentage}%
              </StatNumber>
              <StatLabel style={{ fontSize: '0.9rem', margin: '0' }}>of your tweets mention Bitcoin</StatLabel>
            </div>
            
            {/* First Bitcoin Tweet */}
            {firstTweet && (
              <InsightCard style={{ marginBottom: '20px', background: 'rgba(247, 147, 26, 0.1)', border: '1px solid rgba(247, 147, 26, 0.3)' }}>
                <StoryText style={{ fontSize: '0.85rem', textAlign: 'center', marginBottom: '10px' }}>
                  <strong>🎯 First Bitcoin Mention:</strong>
                </StoryText>
                <StoryText style={{ 
                  fontSize: '0.8rem', 
                  fontStyle: 'italic', 
                  textAlign: 'center', 
                  lineHeight: '1.4',
                  marginBottom: '10px'
                }}>
                  "{firstTweet.tweet_text.length > 100 ? firstTweet.tweet_text.substring(0, 100) + '...' : firstTweet.tweet_text}"
                </StoryText>
                <StoryText style={{ fontSize: '0.7rem', color: '#f7931a', textAlign: 'center' }}>
                  {new Date(firstTweet.tweet_created_at).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </StoryText>
              </InsightCard>
            )}
            
            {/* Journey Stats */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '12px', 
              marginBottom: '20px',
              maxWidth: '300px',
              width: '100%'
            }}>
              <InsightCard style={{ padding: '12px', textAlign: 'center' }}>
                <StatNumber style={{ fontSize: '1.3rem', color: '#f7931a', margin: '0 0 3px 0' }}>
                  {formatNumber(bitcoinData.stats.bitcoin_tweets)}
                </StatNumber>
                <StatLabel style={{ fontSize: '0.75rem', margin: '0' }}>Bitcoin Tweets</StatLabel>
              </InsightCard>
              
              <InsightCard style={{ padding: '12px', textAlign: 'center' }}>
                <StatNumber style={{ fontSize: '1.3rem', color: '#f7931a', margin: '0 0 3px 0' }}>
                  {bitcoinData.yearlyProgression ? Object.keys(bitcoinData.yearlyProgression).length : '1+'}
                </StatNumber>
                <StatLabel style={{ fontSize: '0.75rem', margin: '0' }}>Years Talking BTC</StatLabel>
              </InsightCard>
            </div>
            
            <StoryText style={{ fontSize: '0.8rem', textAlign: 'center', color: '#888' }}>
              {bitcoinData.bitcoinPercentage >= 70 ? 'You\'re a true Bitcoin maximalist! 🧡' : 
               bitcoinData.bitcoinPercentage >= 40 ? 'Solid Bitcoin enthusiasm! 💪' : 
               bitcoinData.bitcoinPercentage >= 20 ? 'Growing Bitcoin interest 📈' : 
               'Bitcoin curious - welcome to the journey! 👋'}
            </StoryText>
          </SlideContainer>
        );

      case 'leaderboard_ranking':
        const rankingData = slide.data;
        // Normalize both usernames by removing @ for comparison since DB stores without @
        const normalizedSearchUsername = rankingData.username?.toLowerCase().replace('@', '') || '';
        const userRanking = rankingData.rankings?.find(r => 
          r.username && normalizedSearchUsername && 
          r.username.toLowerCase() === normalizedSearchUsername
        );
        const topRankings = rankingData.rankings?.slice(0, 3) || [];
        
        return (
          <SlideContainer>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px', marginTop: '15px' }}>
              <img 
                src="/POP_new_logopng.png" 
                alt="ProofOfPrice" 
                style={{ 
                  width: '28px', 
                  height: '28px', 
                  marginRight: '10px',
                  filter: 'brightness(1.1)'
                }} 
              />
              <SubTitle style={{ margin: '0' }}>🏆 Leaderboard</SubTitle>
            </div>
            <BigTitle style={{ fontSize: 'clamp(1.4rem, 5vw, 1.8rem)', marginBottom: '20px' }}>
              Where You Rank
            </BigTitle>
            
            {userRanking ? (
              <>
                {/* User's Rank */}
                <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                  <StatNumber style={{ 
                    fontSize: 'clamp(2.5rem, 12vw, 4rem)',
                    background: userRanking.rank <= 10 ? 'linear-gradient(45deg, #FFD700, #FFA500)' : 'linear-gradient(45deg, #f7931a, #ff6b00)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    margin: '0 0 3px 0'
                  }}>
                    #{userRanking.rank}
                  </StatNumber>
                  <StatLabel style={{ fontSize: '0.9rem', margin: '0' }}>Global Rank</StatLabel>
                </div>
                
                {/* User Stats */}
                <InsightCard style={{ 
                  marginBottom: '15px',
                  background: userRanking.rank <= 10 ? 'rgba(255, 215, 0, 0.1)' : 'rgba(247, 147, 26, 0.1)',
                  border: userRanking.rank <= 10 ? '1px solid rgba(255, 215, 0, 0.3)' : '1px solid rgba(247, 147, 26, 0.3)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                      <StatNumber style={{ fontSize: '1.5rem', color: '#00ff88', margin: '0 0 3px 0' }}>
                        {userRanking.accuracy_percentage}%
                      </StatNumber>
                      <StatLabel style={{ fontSize: '0.75rem', margin: '0' }}>Accuracy</StatLabel>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <StatNumber style={{ fontSize: '1.5rem', margin: '0 0 3px 0' }}>
                        {userRanking.total_predictions}
                      </StatNumber>
                      <StatLabel style={{ fontSize: '0.75rem', margin: '0' }}>Predictions</StatLabel>
                    </div>
                  </div>
                </InsightCard>
              </>
            ) : (
              <>
                <InsightCard style={{ marginBottom: '20px', background: 'rgba(247, 147, 26, 0.1)', border: '1px solid rgba(247, 147, 26, 0.3)' }}>
                  <StoryText style={{ textAlign: 'center', fontSize: '0.9rem', marginBottom: '15px' }}>
                    🎯 Not ranked yet - make some predictions to join the leaderboard!
                  </StoryText>
                  <SubmitButton 
                    onClick={() => {
                      setShowSubmissionForm(true);
                      setCurrentSlide(0); // Reset to submission form
                    }}
                    style={{ 
                      fontSize: '0.85rem',
                      padding: '10px 20px',
                      width: '100%'
                    }}
                  >
                    📊 Submit Your First Prediction
                  </SubmitButton>
                </InsightCard>
              </>
            )}
            
            {/* Top 5 Leaderboard */}
            {topRankings.length > 0 && (
              <>
                <SubTitle style={{ fontSize: '1rem', marginBottom: '15px' }}>🥇 Top Predictors</SubTitle>
                <div style={{ width: '100%', maxWidth: '360px' }}>
                  {topRankings.map((ranking, index) => (
                    <div 
                      key={ranking.username}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        marginBottom: '8px',
                        background: (ranking.username && normalizedSearchUsername && ranking.username.toLowerCase() === normalizedSearchUsername) ? 
                          'rgba(247, 147, 26, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px',
                        border: (ranking.username && normalizedSearchUsername && ranking.username.toLowerCase() === normalizedSearchUsername) ? 
                          '1px solid rgba(247, 147, 26, 0.4)' : '1px solid rgba(255, 255, 255, 0.1)',
                        fontSize: '0.85rem'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <span style={{ 
                          marginRight: '8px',
                          fontSize: '0.85rem',
                          fontWeight: 'bold',
                          color: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : '#888'
                        }}>
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                        </span>
                        <span style={{ 
                          fontWeight: (ranking.username && rankingData.username && ranking.username.toLowerCase() === rankingData.username.toLowerCase()) ? '600' : '400',
                          fontSize: '0.8rem'
                        }}>
                          @{ranking.username}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ color: '#00ff88', fontSize: '0.75rem', fontWeight: '600' }}>
                          {ranking.accuracy_percentage}%
                        </span>
                        <span style={{ color: '#888', fontSize: '0.7rem' }}>
                          {ranking.successful_predictions}/{ranking.completed_predictions}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            
            {/* Worst Predictors Section */}
            {rankingData.worstPerformers && rankingData.worstPerformers.length > 0 && (
              <>
                <SubTitle style={{ fontSize: '1rem', marginBottom: '15px', marginTop: '15px' }}>💀 Worst Predictors</SubTitle>
                <div style={{ width: '100%', maxWidth: '360px' }}>
                  {rankingData.worstPerformers.slice(0, 3).map((ranking, index) => {
                    // Skip entries without username
                    if (!ranking.username) return null;
                    
                    return (
                      <div 
                        key={ranking.username || ranking.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 12px',
                          marginBottom: '8px',
                          background: (ranking.username && rankingData.username && ranking.username.toLowerCase() === rankingData.username.toLowerCase()) ? 
                            'rgba(255, 68, 68, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '8px',
                          border: (ranking.username && rankingData.username && ranking.username.toLowerCase() === rankingData.username.toLowerCase()) ? 
                            '1px solid rgba(255, 68, 68, 0.4)' : '1px solid rgba(255, 255, 255, 0.1)',
                          fontSize: '0.85rem'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                          <span style={{ 
                            marginRight: '8px',
                            fontSize: '0.85rem',
                            fontWeight: 'bold',
                            color: '#ff4444'
                          }}>
                            #{index + 1}
                          </span>
                          <span style={{ 
                            fontWeight: (ranking.username && rankingData.username && ranking.username.toLowerCase() === rankingData.username.toLowerCase()) ? '600' : '400',
                            fontSize: '0.8rem'
                          }}>
                            @{ranking.username}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ color: '#ff4444', fontSize: '0.75rem', fontWeight: '600' }}>
                            {ranking.accuracy_percentage}%
                          </span>
                          <span style={{ color: '#888', fontSize: '0.7rem' }}>
                            {ranking.missed_predictions}/{ranking.completed_predictions}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            
            <StoryText style={{ fontSize: '0.8rem', color: '#888', textAlign: 'center', marginTop: '15px' }}>
              Compete with the best Bitcoin predictors! 🎯
            </StoryText>
          </SlideContainer>
        );

      case 'personality_insights':
        // Dynamic font sizing based on content length - no truncation needed
        const roastContent = userRoast?.content || '';
        // Scale font: long roasts get smaller font to fit (increased sizes)
        const roastFontSize = roastContent.length > 600 ? '1rem' 
          : roastContent.length > 400 ? '1.1rem' 
          : roastContent.length > 200 ? '1.2rem' 
          : '1.3rem';
        const roastLineHeight = roastContent.length > 400 ? '1.4' : '1.5';
        
        return (
          <SlideContainer>
            <SubTitle style={{ fontSize: '1.3rem' }}>🔥 Roast Time 🔥</SubTitle>
            
            {userRoast ? (
              <>
                <BigTitle style={{ 
                  fontSize: 'clamp(1.1rem, 4vw, 1.4rem)', 
                  marginBottom: '20px', 
                  textAlign: 'center',
                  lineHeight: '1.3',
                  maxWidth: '95%'
                }}>
                  {userRoast.title}
                </BigTitle>
                
                <div style={{
                  position: 'relative',
                  width: 'auto',
                  maxWidth: '500px',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  {/* Left flame */}
                  <div style={{
                    position: 'absolute',
                    left: '-30px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '2rem',
                    opacity: 0.6,
                    filter: 'drop-shadow(0 0 8px rgba(247, 147, 26, 0.5))'
                  }}>🔥</div>
                  
                  <InsightCard style={{ 
                    marginBottom: '15px',
                    background: 'linear-gradient(135deg, rgba(247, 147, 26, 0.15) 0%, rgba(255, 100, 50, 0.1) 100%)', 
                    border: '2px solid rgba(247, 147, 26, 0.4)',
                    borderRadius: '16px',
                    padding: '20px 25px',
                    width: 'auto',
                    maxWidth: '500px',
                    boxShadow: '0 0 20px rgba(247, 147, 26, 0.2), inset 0 0 30px rgba(247, 147, 26, 0.05)'
                  }}>
                    <StoryText style={{ 
                      fontSize: roastFontSize, 
                      lineHeight: roastLineHeight,
                      textAlign: 'left'
                    }}>
                      {roastContent}
                    </StoryText>
                  </InsightCard>
                  
                  {/* Right flame */}
                  <div style={{
                    position: 'absolute',
                    right: '-30px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '2rem',
                    opacity: 0.6,
                    filter: 'drop-shadow(0 0 8px rgba(247, 147, 26, 0.5))'
                  }}>🔥</div>
                </div>
                
                <StoryText style={{ fontSize: '0.75rem', color: '#f7931a', textAlign: 'center', marginTop: '10px' }}>
                  Roasted with love by ProofOfPrice 🧡
                </StoryText>
              </>
            ) : (
              <StoryText style={{ fontSize: '1rem', color: '#888', textAlign: 'center' }}>
                No roast available yet! 🔥
              </StoryText>
            )}
          </SlideContainer>
        );

      case 'share_results':
        const shareData = slide.data;
        const top3Words = wordCloudData.slice(0, 3).map(w => w.text).join(', ');
        
        // Main stats share text
        const shareText = `🧡 My #BitcoinWrapped by @ProofOfPrice:\n\n` +
          `💬 ${shareData.bitcoinPercentage}% of my tweets mention Bitcoin\n` +
          `📊 Prediction Accuracy: ${shareData.predictionData?.stats?.success_rate || 0}%\n` +
          `🔑 Most used words: ${top3Words}\n\n` +
          `Get your Bitcoin Wrapped: https://proofofprice.com/wrapped`;
        
        // Separate roast share text
        const roastShareText = userRoast ? 
          `🔥 My Bitcoin Roast by @ProofOfPrice:\n\n"${userRoast.title}"\n\n${userRoast.content.slice(0, 200)}${userRoast.content.length > 200 ? '...' : ''}\n\nGet roasted: https://proofofprice.com/wrapped` : 
          '';
        
        return (
          <SlideContainer>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
              <img 
                src="/POP_new_logopng.png" 
                alt="ProofOfPrice" 
                style={{ 
                  width: '36px', 
                  height: '36px', 
                  marginRight: '12px',
                  filter: 'brightness(1.1)'
                }} 
              />
              <BigTitle style={{ margin: '0', fontSize: 'clamp(1.6rem, 6vw, 2.2rem)' }}>
                Share Your Results
              </BigTitle>
            </div>
            
            <SubTitle style={{ marginBottom: '25px', textAlign: 'center' }}>🎉 Spread the Bitcoin Love</SubTitle>
            
            {/* Share Buttons */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '12px',
              width: '100%',
              maxWidth: '300px',
              marginBottom: '25px'
            }}>
              <SubmitButton 
                onClick={() => {
                  trackEvent('wrapped-share', { username, type: 'stats-slide' });
                  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
                  window.open(twitterUrl, '_blank');
                }}
                style={{
                  background: 'linear-gradient(45deg, #1DA1F2, #0d8bd9)',
                  fontSize: '0.9rem',
                  padding: '14px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                🐦 Share Stats
              </SubmitButton>
              
              {userRoast && (
                <SubmitButton 
                  onClick={() => {
                    trackEvent('wrapped-share', { username, type: 'roast-slide' });
                    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(roastShareText)}`;
                    window.open(twitterUrl, '_blank');
                  }}
                  style={{
                    background: 'linear-gradient(45deg, #ff6b00, #f7931a)',
                    fontSize: '0.9rem',
                    padding: '14px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  🔥 Share Roast
                </SubmitButton>
              )}
              
              <SubmitButton 
                onClick={() => {
                  window.open('https://proofofprice.com', '_blank');
                }}
                style={{
                  background: 'linear-gradient(45deg, #f7931a, #ff6b00)',
                  fontSize: '0.9rem',
                  padding: '14px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                🚀 Visit ProofOfPrice.com
              </SubmitButton>
            </div>

            {/* Dashboard Teaser */}
            <InsightCard style={{ 
              background: 'linear-gradient(135deg, rgba(247, 147, 26, 0.1), rgba(255, 183, 77, 0.05))', 
              border: '2px solid rgba(247, 147, 26, 0.3)',
              maxWidth: '350px',
              width: '100%',
              marginTop: '20px'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                <BigTitle style={{ fontSize: '1.2rem', margin: '0 0 6px 0' }}>
                  🚀 Go Deeper
                </BigTitle>
              </div>
              
              <div style={{ 
                fontSize: '0.8rem', 
                lineHeight: '1.5', 
                color: '#e0e0e0',
                textAlign: 'left',
                marginBottom: '12px',
                paddingLeft: '5px'
              }}>
                <div style={{ marginBottom: '5px' }}>📊 Get <strong>ALL</strong> your tweets analyzed</div>
                <div style={{ marginBottom: '5px' }}>🔍 <strong>Deeper insights</strong> than this preview</div>
                <div style={{ marginBottom: '5px' }}>🎯 <strong>Dashboard</strong> to manage your predictions</div>
                <div style={{ marginBottom: '5px' }}>🔮 Early access to <strong>new features</strong></div>
              </div>
              
              {/* Sign in with X Button */}
              <SubmitButton
                onClick={() => {
                  if (isAuthenticated) {
                    const token = localStorage.getItem('jwt_token');
                    if (token) {
                      window.location.href = `/pricing?token=${token}&upgrade=true`;
                    } else {
                      window.location.href = '/api/auth/x/login';
                    }
                  } else {
                    window.location.href = '/api/auth/x/login';
                  }
                }}
                style={{
                  background: isAuthenticated 
                    ? 'linear-gradient(135deg, #f7931a 0%, #ffb74d 100%)'
                    : 'linear-gradient(135deg, #1DA1F2 0%, #0d8bd9 100%)',
                  fontSize: '0.9rem',
                  padding: '14px 24px',
                  width: '100%',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '10px'
                }}
              >
                {isAuthenticated ? (
                  <>⚡ Upgrade Now</>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" style={{ width: '18px', height: '18px', fill: 'currentColor' }}>
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Sign in with X
                  </>
                )}
              </SubmitButton>
              
              <StoryText style={{ fontSize: '0.7rem', color: '#888', textAlign: 'center', margin: '0 0 12px 0' }}>
                Pay with Lightning ⚡
              </StoryText>
              
              {/* Email waitlist for non-X users */}
              {!isAuthenticated && (
                <div style={{ 
                  marginTop: '12px', 
                  paddingTop: '12px',
                  borderTop: '1px solid rgba(255,255,255,0.1)'
                }}>
                  <StoryText style={{ fontSize: '0.75rem', color: '#aaa', marginBottom: '10px', textAlign: 'center' }}>
                    No X account? Join our waitlist:
                  </StoryText>
                  
                  {waitlistStatus === 'success' ? (
                    <StoryText style={{ fontSize: '0.8rem', color: '#4CAF50', textAlign: 'center' }}>
                      {waitlistMessage}
                    </StoryText>
                  ) : (
                    <>
                      <input
                        type="email"
                        placeholder="your@email.com"
                        value={waitlistEmail}
                        onChange={(e) => {
                          setWaitlistEmail(e.target.value);
                          if (waitlistStatus === 'error') setWaitlistStatus('idle');
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          fontSize: '0.85rem',
                          background: 'rgba(0, 0, 0, 0.3)',
                          border: waitlistStatus === 'error' ? '1px solid #ff6b6b' : '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '8px',
                          color: '#fff',
                          marginBottom: '8px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        onKeyPress={(e) => e.key === 'Enter' && submitWaitlist()}
                      />
                      
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        fontSize: '0.7rem',
                        color: '#888',
                        marginBottom: '10px',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={newsletterOptIn}
                          onChange={(e) => setNewsletterOptIn(e.target.checked)}
                          style={{ width: '14px', height: '14px', accentColor: '#f7931a' }}
                        />
                        Keep me updated on new features
                      </label>
                      
                      {waitlistStatus === 'error' && (
                        <StoryText style={{ fontSize: '0.7rem', color: '#ff6b6b', marginBottom: '8px' }}>
                          {waitlistMessage}
                        </StoryText>
                      )}
                      
                      <SubmitButton
                        onClick={submitWaitlist}
                        disabled={waitlistStatus === 'loading'}
                        style={{
                          background: 'linear-gradient(135deg, #f7931a 0%, #ffb74d 100%)',
                          fontSize: '0.8rem',
                          padding: '10px 16px',
                          width: '100%',
                          opacity: waitlistStatus === 'loading' ? 0.7 : 1
                        }}
                      >
                        {waitlistStatus === 'loading' ? 'Joining...' : '📧 Join Waitlist'}
                      </SubmitButton>
                    </>
                  )}
                </div>
              )}
            </InsightCard>

            {/* Back to Start Button */}
            <div 
              onClick={() => scrollToSlide(0)}
              style={{
                marginTop: '30px',
                padding: '12px 24px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '30px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontSize: '0.85rem',
                color: '#fff',
                transition: 'all 0.3s ease',
                opacity: 0.8
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(247, 147, 26, 0.2)';
                e.currentTarget.style.borderColor = 'rgba(247, 147, 26, 0.5)';
                e.currentTarget.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.opacity = '0.8';
              }}
            >
              <span style={{ fontSize: '1.1rem' }}>⬆️</span>
              <span>Back to Start</span>
            </div>

          </SlideContainer>
        );

      default:
        return <SlideContainer><StoryText>Loading...</StoryText></SlideContainer>;
    }
  };

  return (
    <AppContainer>
      <GlobalStyles />
      
      {/* ProofOfPrice Logo */}
      <LogoContainer>
        <img 
          src="/POP_new_logopng.png" 
          alt="ProofOfPrice.com" 
          onClick={() => window.open('https://proofofprice.com', '_blank')}
        />
      </LogoContainer>
      
      {/* Two-part experience: Welcome OR Wrapped */}
      {!analytics ? (
        /* WELCOME SCREEN - Entry point */
        <div style={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          boxSizing: 'border-box'
        }}>
          {renderSlide({ type: 'welcome' })}
        </div>
      ) : (
        /* WRAPPED CONTENT - Shareable slideshow */
        <>
          <div 
            className="scroll-container"
            style={{
              width: '100%',
              height: '100vh',
              overflowY: 'auto',
              scrollBehavior: 'smooth',
              position: 'relative'
            }}
            onScroll={handleScroll}
          >
            {slides.map((slide, index) => (
              <div key={slide.id} style={{ 
                height: '100vh',
                width: '100%',
                scrollSnapAlign: 'start',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                boxSizing: 'border-box'
              }}>
                {renderSlide(slide)}
              </div>
            ))}
          </div>

          {/* Progress indicators - Only show in wrapped mode */}
          <div style={{
            position: 'fixed',
            right: '20px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            zIndex: 1000
          }}>
            {slides.map((slide, index) => (
              <div
                key={slide.id}
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: index === currentSlide ? '#f7931a' : 'rgba(255, 255, 255, 0.3)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => scrollToSlide(index)}
              />
            ))}
          </div>
        </>
      )}
    </AppContainer>
  );
};

export default BitcoinWrapped;
